import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import TopBar from "../components/risikoanalyseStep/TopBar";
import Sidebar from "../components/risikoanalyseStep/Sidebar";
import TocMenu from "../components/risikoanalyseStep/TocMenu";
import { useTocLayout, type TocMode } from "../components/risikoanalyseStep/useTocLayout";
import { useTocLeistenOffset } from "../components/risikoanalyseStep/useTocLeistenOffset";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import { useIsPageChrome } from "../components/windowManager/pageChrome";
import PageModeControls from "../components/windowManager/PageModeControls";
import { kontextZeileText } from "../components/risikoImmobilieStep/kontextzeile";
import {
  scrollFormToTop,
  scrollScrollableAncestorsToTop,
} from "../components/risikoanalyseStep/scrollToTop";
import QuestionBlock from "../components/risikoanalyseStep/QuestionBlock";
import QuestionField from "../components/risikoanalyseStep/QuestionField";
import type {
  QuestionDef,
  Answers,
} from "../components/risikoanalyseStep/types";
import AnschriftBlock from "../components/risikoImmobilieStep/AnschriftBlock";
import GebaeudeRegistry from "../components/risikoImmobilieStep/GebaeudeRegistry";
import KategoriePicker from "../components/risikoImmobilieStep/KategoriePicker";
import TypPicker from "../components/risikoImmobilieStep/TypPicker";
import BauartklassePicker from "../components/risikoImmobilieStep/BauartklassePicker";
import StellungPicker from "../components/risikoImmobilieStep/StellungPicker";
import GeschossStepper from "../components/risikoImmobilieStep/GeschossStepper";
import {
  GESCHOSS_PAAR_TESTID,
  geschossPaarStil,
} from "../components/risikoImmobilieStep/geschossPaarLayout";
import GrossZahlEingabe from "../components/risikoImmobilieStep/GrossZahlEingabe";
import DachPicker from "../components/risikoImmobilieStep/DachPicker";
import EinheitenList, {
  type Einheit,
} from "../components/risikoImmobilieStep/EinheitenList";
import BranchenChecklist, {
  type BranchenSelections,
} from "../components/risikoImmobilieStep/BranchenChecklist";
import HeizungsChecklist, {
  type HeizungSelections,
} from "../components/risikoImmobilieStep/HeizungsChecklist";
import UmweltChecklist, {
  type UmweltSelections,
} from "../components/risikoImmobilieStep/UmweltChecklist";
import AnlagenBlock, {
  type AnlagenEntry,
} from "../components/risikoImmobilieStep/AnlagenBlock";
import AnlagenChecklist from "../components/risikoImmobilieStep/AnlagenChecklist";
import {
  ANLAGEN_AUSWAHL_FRAGE,
  ANLAGEN_GRUPPEN,
  anlagenAnswerKey,
  anlagenDerGruppe,
  anlagenSlotKey,
  zeigtErrichter,
} from "../components/risikoImmobilieStep/anlagenKatalog";
import UploadField from "../components/risikoImmobilieStep/UploadField";
import StepHero from "../components/risikoImmobilieStep/StepHero";
import {
  AUSSTATTUNGS_STUFEN,
  SONDERAUSSTATTUNGEN,
  UNTERLAGEN_ITEMS,
  UNTERLAGEN_STATUS,
  VERWALTUNGS_ARTEN,
  WELLNESS_EINRICHTUNGEN,
  EIGENNUTZUNG_OPTIONS,
} from "../components/risikoImmobilieStep/enums";
import AusstattungsstufePicker from "../components/risikoImmobilieStep/AusstattungsstufePicker";
import AusstattungsKatalog from "../components/risikoImmobilieStep/AusstattungsKatalog";
import StellplatzBlock from "../components/risikoImmobilieStep/StellplatzBlock";
import {
  asStellplatzArten,
  asStellplatzDetails,
  geschossOptionen,
  stellplatzArtenText,
} from "../components/risikoImmobilieStep/stellplatzTypes";
import SanierungListe, {
  SANIERUNG_ALT_SCHLUESSEL,
  SANIERUNG_UMFANG_OPTIONEN,
  sanierungenAusAntworten,
  type SanierungEintrag,
} from "../components/risikoImmobilieStep/SanierungListe";
import KunstListe, {
  KUNST_ALT_SCHLUESSEL,
  kunstAusAntworten,
  type KunstObjekt,
} from "../components/risikoImmobilieStep/KunstListe";
import {
  anlageSichtbar,
  ausstattungSichtbar,
  notstromSichtbar,
} from "../components/risikoImmobilieStep/nutzungsWeiche";
import KundenlinkKarte from "../components/risikoImmobilieStep/KundenlinkKarte";
import VersicherungsbedarfBlock from "../components/risikoImmobilieStep/VersicherungsbedarfBlock";
import {
  alsKundenlinkImmobilie,
  teileImmobilienMerkmale,
  type KundenlinkImmobilie,
} from "../components/risikoImmobilieStep/kundenlinkDaten";
import AnbautenListe, {
  ANBAU_ALT_SCHLUESSEL,
  anbautenAusAntworten,
  type Anbau,
} from "../components/risikoImmobilieStep/AnbautenListe";
import SchadenListe, {
  asSchadenListe,
} from "../components/risikoImmobilieStep/SchadenListe";
import GesamtflaecheBlock from "../components/risikoImmobilieStep/GesamtflaecheBlock";
import { profileForType } from "../components/risikoImmobilieStep/unitFollowUpSpec";
import { parseStructuredAnswers } from "../components/risikoImmobilieStep/structuredAnswers";
import {
  migriereAltAntworten,
  migriereAusstattung,
} from "../components/risikoImmobilieStep/altAntworten";
import AdressFelder from "../components/risikoImmobilieStep/AdressFelder";
import KontaktFeld from "../components/risikoImmobilieStep/KontaktFeld";
import BeteiligtenListe from "../components/risikoImmobilieStep/BeteiligtenListe";
import { asBeteiligtenListe, asKontaktRef } from "../components/risikoImmobilieStep/beteiligteTypes";
import { ladeKontaktStammdaten } from "../components/risikoImmobilieStep/kontaktStammdaten";
import {
  AnsprechpartnerAuswahl,
  HauskontoAuswahl,
} from "../components/risikoImmobilieStep/KontaktAuswahlFelder";
import {
  anspracheFor,
  immoText,
  type Ansprache,
} from "../components/risikoImmobilieStep/texte";
import {
  GLOBAL_SCOPE,
  UPLOAD_DISABLED,
  UploadBufferProvider,
  useUploadBufferState,
  type PendingUpload,
  type UploadMode,
} from "../components/risikoImmobilieStep/uploadBuffer";
import type {
  ContactPrefill,
  Gebaeude,
  ImmoStep,
  ImmoStepKind,
} from "../components/risikoImmobilieStep/types";
import {
  buildImmoSections,
  buildImmoSteps,
  istGewerblich,
  locateInSections,
  titleForImmoStepKind as titleForKind,
} from "../components/risikoImmobilieStep/gliederung";

/** Behälter des Schritt-Inhalts — Anker des Kettentests, siehe Nutzungsstelle. */
export const SCHRITT_INHALT_TESTID = "schritt-inhalt";

// Der Draft speichert `stepIdx`. Jede Änderung der Schrittkette entwertet diese
// Zahl, deshalb wird der Key mitgezogen:
//   v2 — der Eigentümer-Step wurde in „Eigentum & Rechte" + „Beteiligte am Objekt" gespalten.
//   v3 — je Gebäude kamen „Gutachten" und „Finanzierung / Sicherungsrechte" VOR
//        „Unterlagen" dazu; ein alter Entwurf würde sonst an der falschen Stelle
//        wieder aufsetzen (bei zwei Gebäuden um vier Schritte verschoben).
//   v4 — drei Schritte sind in ihre Nachbarn gewandert („Leerstand" in „Bauart",
//        „Spezifizierung" der Einheiten und „Kunst" in „Anzahl der Einheiten").
//        Die Kette schrumpft je Gebäude von 19 auf 16 Arten; ein v3-Entwurf
//        setzte sonst bis zu sechs Schritte zu weit hinten wieder auf. Die
//        ANTWORTEN sind unverändert gültig und werden über die Legacy-Liste
//        übernommen — verworfen wird nur die gespeicherte Schrittnummer.
//   v5 — „Schadenprävention" ist als eigener Schritt zwischen „Sicherheit" und
//        „Energie" dazugekommen. Die Kette je Gebäude wächst von 16 auf 17
//        Arten; ein v4-Entwurf setzte sonst bei jedem Gebäude einen Schritt zu
//        früh wieder auf und übersprünge am Ende die Zusammenfassung.
export const DRAFT_LS_PREFIX = "risikoimmobilie-draft-v5:";
/**
 * Vorgänger-Keys, neueste zuerst. Beim Laden wird der erste vorhandene einmalig
 * übernommen — nur die Antworten, nie der `stepIdx` — und danach werden alle
 * Alt-Einträge entfernt.
 */
export const LEGACY_DRAFT_LS_PREFIXES = [
  "risikoimmobilie-draft-v4:",
  "risikoimmobilie-draft-v3:",
  "risikoimmobilie-draft-v2:",
  "risikoimmobilie-draft:",
];
const SIDEBAR_WIDTH = 288;
const BOTTOM_BAR_RESERVE = 110;
const AUTOSAVE_DEBOUNCE_MS = 1500;

/**
 * Leerstand — bis 19.08.2026 ein eigener Schritt, jetzt der letzte Block der
 * „Bauart". Die Schlüssel stecken in Bestandsdaten und bleiben unverändert.
 *
 * Seit 10/2026 fragt der Block „Bis wann?" und trennt Wiedervermietung und
 * Sanierung („Das sind zwei Paar Schuhe", Fachabstimmung 31.08.2026). Das
 * frühere gemeinsame Freitextfeld `leerstandPlan` erscheint nur noch, wenn ein
 * Bestandswert darin steht — sonst ginge die Angabe beim Bearbeiten verloren.
 */
/** Kellernutzung — nur bei Gewerbe/Mischnutzung mit Keller gefragt. */
const KELLER_NUTZUNG = ["Lager", "gewerblich genutzt", "Lager und gewerblich", "sonstiges"] as const;

/** Wohnfläche gibt es nur, wo gewohnt wird: nicht bei reinem Gewerbe. */
const wohnflaecheGefragt = (a: Record<string, unknown>): boolean =>
  (a.kategorie as string | undefined) !== "Gewerbe (100%)";

/** Kundenlink: so lange nach der letzten Eingabe wird ins Risiko gespeichert. */
const KUNDENLINK_SPEICHER_MS = 2500;

const leerstandFrage = (mitAltPlan: boolean): QuestionDef => ({
  ...LEERSTAND_FRAGE,
  followUpFields: mitAltPlan
    ? [
        ...(LEERSTAND_FRAGE.followUpFields ?? []),
        {
          key: "leerstandPlan",
          label: "Bisherige Angabe zu Wiedervermietung / Sanierung",
          type: "text",
        },
      ]
    : LEERSTAND_FRAGE.followUpFields,
});

const LEERSTAND_FRAGE: QuestionDef = {
  question: "Besteht derzeit Leerstand im Gebäude?",
  field: { key: "leerstand", type: "yesno" },
  showFollowUpWhen: { key: "leerstand", equals: "ja" },
  followUpFields: [
    { key: "leerstandTeile", label: "Welche Gebäudeteile sind betroffen?", type: "text" },
    { key: "leerstandSeit", label: "Seit wann?", type: "date" },
    { key: "leerstandBis", label: "Bis wann voraussichtlich?", type: "date" },
    {
      key: "leerstandUmfang",
      label: "Umfang",
      type: "select",
      options: [
        { value: "vollständig", label: "vollständig" },
        { value: "teilweise", label: "teilweise" },
      ],
    },
    { key: "leerstandAnteil", label: "Leerstandsanteil (%)", type: "number" },
    { key: "leerstandGrund", label: "Aus welchem Grund?", type: "textarea" },
    {
      key: "leerstandDauer",
      label: "Vorübergehend oder längerfristig?",
      type: "select",
      options: [
        { value: "vorübergehend", label: "vorübergehend" },
        { value: "längerfristig", label: "längerfristig" },
      ],
    },
    { key: "leerstandWiedervermietungAb", label: "Wiedervermietung geplant ab", type: "date" },
    { key: "leerstandSanierungAb", label: "Sanierung geplant ab", type: "date" },
  ],
};

/**
 * Kunst am Bau — bis 19.08.2026 ein eigener Schritt, jetzt der letzte Block der
 * „Anzahl der Einheiten". Schlüssel unverändert (`kunst`, `kunstBeschreibung`,
 * `kunstWert`).
 */
const KUNST_FRAGE: QuestionDef = {
  question:
    "Ist im Freien oder am Gebäude Kunst am Bau vorhanden? (z. B. Skulpturen, Reliefs)",
  field: { key: "kunst", type: "yesno" },
};
/**
 * Die drei Präventionsblöcke des Schritts „Schadenprävention" (Auftraggeber,
 * 19.08.2026). Alle drei tragen dieselben zwei Folgefelder, deshalb EINE
 * Vorlage und eine Liste — dreimal derselbe abgeschriebene Block wäre die
 * Stelle, an der beim nächsten Zusatzfeld einer zurückbleibt.
 *
 * Sie ersetzen den früheren Sammelblock „Früherkennungs- oder
 * Überwachungssysteme zur Schadenprävention (z. B. Leitungswasserverlust,
 * Überschwemmung)?" (`frueherkennung`, `frueherkennungSysteme`,
 * `frueherkennungGefahren`), der als zusammengequetschte Vorstufe genau diese
 * drei Fälle in eine Frage gepresst hat und ersatzlos entfallen ist.
 */
const PRAEVENTIONS_BLOECKE = [
  { key: "praeventionLeitungswasser", titel: "Erkennung von Leitungswasserverlust" },
  { key: "praeventionUeberschwemmung", titel: "Vorbeugung von Überschwemmungsschäden" },
  { key: "praeventionSonstige", titel: "Sonstige vorbeugende Systeme und Anlagen" },
] as const;

/** `praeventionX` (ja/nein) → `praeventionXArt` (mehrzeilig) + `praeventionXWert`. */
const praeventionsFrage = (block: { key: string; titel: string }): QuestionDef => ({
  question: block.titel,
  field: { key: block.key, type: "yesno" },
  showFollowUpWhen: { key: block.key, equals: "ja" },
  followUpFields: [
    {
      key: `${block.key}Art`,
      label: "Art des Systems bitte beschreiben",
      type: "textarea",
    },
    { key: `${block.key}Wert`, label: "Wert", type: "number" },
  ],
});

const useStyles = makeStyles({
  pageState: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  pageStateCard: {
    maxWidth: "480px",
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "rgba(255,255,255,0.05)",
    textAlign: "center",
    ...shorthands.borderRadius("8px"),
  },
  pageStateTitle: {
    margin: 0,
    marginBottom: "8px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.8rem",
    color: BRAND.gold,
  },
  pageStateBody: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: "rgba(255,255,255,0.8)",
  },
  /**
   * Die Schale trägt eine DEFINITE Höhe (`height`), nicht bloß `min-height`.
   * Genau daran hing das Scroll-Problem (Auftraggeber, 19.08.2026): mit
   * `min-height` bleibt die Höhe für alle Nachfahren UNbestimmt — `flex-grow`
   * und `height: 100%` lösen sich zu Inhaltshöhe auf, `overflow-y: auto` auf
   * `main` lief damit ins Leere und gescrollt wurde der jeweils ÄUSSERE
   * Rahmen: das Dokument in der öffentlichen Strecke, der Fenster-Body im
   * Backoffice. Also zwei verschiedene Scrollports für dieselbe Maske, und
   * obendrauf Hilfskonstruktionen (Verzeichnis `position: sticky` mit
   * gemessener Höhe), damit die Ränder überhaupt mitwandern.
   *
   * Vorbild ist die Risikoanalyse (`RisikoanalyseStepForm`, Stile `shell` /
   * `body` / `main`): definite Höhe + `overflow: hidden` oben, `min-height: 0`
   * auf jedem Flex-Nachfahren, und genau EIN Scroll-Container (`main`).
   *
   * `100dvh` als zweiter Wert: auf Mobilgeräten meint `100vh` die
   * AUSGEFAHRENE Viewport-Höhe — die Schale ragte damit unter die
   * eingeblendete Browser-Leiste. Browser ohne `dvh` behalten den ersten Wert.
   */
  shell: {
    height: ["100vh", "100dvh"],
    maxHeight: ["100vh", "100dvh"],
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
  },
  /**
   * Eingebettet gibt der RAHMEN die Höhe vor, nicht der Viewport: im
   * Backoffice der Fenster-Body (FloatingWindow, `overflow: auto`, feste
   * Flex-Höhe) über die Vollflächen-Schale von `NewRiskPage`. `100vh` wäre
   * dort systematisch falsch — der Viewport ist groß, ein schwebendes
   * Fenster nicht.
   *
   * `flexGrow`/`minHeight` greifen nur, wenn der Rahmen selbst eine
   * Flex-Spalte ist (genau der Fall in `NewRiskPage`); außerhalb kosten sie
   * nichts. Und wo der Rahmen GAR KEINE definite Höhe hat — die öffentliche
   * Detail-Strecke rendert die Maske in einer gewöhnlichen Dokumentseite —,
   * löst `100%` sich regelkonform zu `auto` auf: dort bleibt es beim
   * bisherigen Dokument-Scroll.
   */
  shellEmbedded: {
    height: "100%",
    maxHeight: "100%",
    flexGrow: 1,
    minHeight: 0,
  },
  /**
   * `hoehe="inhalt"` — die Maske als Karte in einer Dokumentseite. Höhe und
   * `overflow` gehen exakt auf den Stand vor dem Vollflächen-Umbau zurück:
   * die Schale wächst mit dem Inhalt, gescrollt wird das Dokument, und die
   * Schritt-Leiste findet mit `position: sticky` wieder einen Scrollport.
   */
  shellInhalt: {
    height: "auto",
    maxHeight: "none",
    flexGrow: 0,
    overflow: "visible",
  },
  bodyInhalt: { overflow: "visible" },
  mainInhalt: { overflowY: "visible", overflowX: "visible" },
  /**
   * `minHeight: 0` ist hier Pflicht, nicht Kosmetik: die automatische
   * Mindesthöhe eines Flex-Kindes ist seine INHALTShöhe. Ohne die 0 drückt der
   * Inhalt `body` über die Schale hinaus und `overflow: hidden` schnitte unten
   * ab, statt dass `main` scrollt.
   */
  body: { flexGrow: 1, display: "flex", overflow: "hidden", minHeight: 0 },
  /**
   * Ob das Verzeichnis dauerhaft steht, entscheidet jetzt die gemessene
   * CONTAINER-Breite (`useTocLayout`) — nicht mehr eine Viewport-Media-Query.
   * Im schwebenden Fenster war die schlicht falsch: der Viewport ist breit,
   * das Fenster nicht.
   */
  /**
   * Flex-Spalte statt `block`: das `<nav>` der Sidebar wächst darin per
   * `flex-grow` auf die volle Höhe und scrollt für sich (Sidebar-`root` steht
   * auf `overflow-y: auto`).
   *
   * Seit die Schale eine definite Höhe hat, streckt der Flex-Container das
   * `<aside>` in JEDEM Kontext bis zum unteren Rand. Die frühere
   * Hilfskonstruktion — `position: sticky` plus eine inline gesetzte,
   * gemessene Höhe des äußeren Scrollports — ist damit weg: sie war nötig,
   * weil eingebettet der Fenster-Body scrollte statt `main`, und sie war in
   * der öffentlichen Vollseite zu groß (sie kannte die Kopfzeile nicht).
   */
  sidebarDesktop: {
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    minHeight: 0,
    width: `${SIDEBAR_WIDTH}px`,
    ...shorthands.borderRight("1px", "solid", "rgba(45,20,43,0.1)"),
  },
  /**
   * DER Scroll-Container der Maske — in allen vier Kontexten derselbe. Vorher
   * scrollte je nach Rahmen etwas anderes (Dokument bzw. Fenster-Body), weil
   * `main` mangels definiter Elternhöhe nie eine begrenzte Höhe bekam.
   *
   * `overflowX: hidden` wie in der Risikoanalyse: die Maske hat keinen einzigen
   * horizontal scrollenden Bereich, ein Ausreißer nach rechts wäre also immer
   * ein Fehler — und würde sonst die ganze Seite seitwärts schiebbar machen.
   */
  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
    overflowX: "hidden",
    minHeight: 0,
  },
  /**
   * `boxSizing: border-box` ist hier NICHT kosmetisch, sondern der Fix für den
   * rechts abgeschnittenen Inhalt (Auftraggeber-Screenshot, 19.08.2026): einen
   * globalen `*`-Reset gibt es in dieser App nicht (`box-sizing: border-box`
   * gilt nur unter `.kvp-rd`, und darunter liegt dieses Formular nicht). Als
   * content-box addierte sich der Innenabstand von 2×32px ZUSÄTZLICH zu
   * `width: 100%` — die Spalte war also immer 64px breiter als der Platz, den
   * sie hat, und lief rechts aus dem Fenster.
   *
   * Damit die sichtbare Spalte überall dort, wo sie bisher gepasst hat,
   * pixelgleich bleibt, wandert der Innenabstand in `maxWidth`: 920 + 2×16 =
   * 952px bzw. ab 768px 920 + 2×32 = 984px. Der Inhalt bleibt also 920px breit,
   * nur der Überhang verschwindet.
   *
   * Die Kappung BLEIBT, obwohl die Schale seit 19.08.2026 die volle Fläche
   * nutzt — nach demselben Vorbild wie die Risikoanalyse, die ihre Lesespalte
   * ebenfalls deckelt (`inner` 780px, `innerWide` 1024px). „Volle Breite"
   * meint die Schale: Verzeichnis, Scrollfläche und Schritt-Leiste dürfen die
   * Fläche ausfüllen. Die Lesespalte dagegen bleibt begrenzt, weil sonst
   * Beschriftung und Eingabefeld auf einem breiten Monitor meterweit
   * auseinanderliefen — und weil die Bauartklassen-Bilder auf genau diese
   * 920px ausgelegt sind (`BauartklassePicker`).
   */
  inner: {
    boxSizing: "border-box",
    width: "100%",
    maxWidth: "952px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "24px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: `${BOTTOM_BAR_RESERVE}px`,
    flexGrow: 1,
    "@media (min-width: 768px)": {
      maxWidth: "984px",
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  innerEmbedded: {
    // BottomBar is sticky and in flow, so no extra reserve needed
    paddingBottom: 0,
  },
  /**
   * Die Pille trug bis 18.08.2026 die rotierende Abschnittsfarbe. Der
   * Auftraggeber wollte es ruhiger: EINE Markenfarbe für alle Abschnitte —
   * dieselbe, die das Inhaltsverzeichnis neutral markiert.
   */
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    backgroundColor: BRAND.purple,
    marginBottom: "8px",
    ...shorthands.borderRadius("999px"),
  },
  /**
   * Kontextzeile unmittelbar über der Abschnittsüberschrift: links ruhig und
   * klein „Immobilie · Anschrift — Kunde", rechts die drei Bedienelemente der
   * Vollseite. Sie ersetzt die gestrichene Formular-Kopfzeile (Auftraggeber,
   * 19.08.2026) — deshalb bewusst KEINE zweite Überschrift-Optik.
   */
  kontextZeile: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "12px",
    minHeight: "32px",
  },
  /**
   * VOLLSEITE: die Kontextzeile bleibt beim Scrollen stehen. An ihr hängt dort
   * die EINZIGE Bedienung des Formulars — FloatingWindow blendet im Zustand
   * „page" die Titelleiste aus (`{!isPage && …}`). Ohne `sticky` wären „Als
   * Fenster", „Minimieren" und „Schließen" nach dem ersten Scrollen weg; genau
   * dagegen war die gestrichene Kopfzeile sticky (`topRowSticky` in
   * NewRiskPage).
   *
   * Scroll-Container ist seit 19.08.2026 `main` — der einzige Scrollport der
   * Maske (vorher scrollte der Fenster-Body, und `body`/`main` standen dafür
   * eingebettet auf `overflow: visible`). `top: 0` klebt jetzt also an der
   * Oberkante der Inhaltsfläche statt an der des Fensters; sichtbar ändert
   * sich dadurch nichts, weil die Schale den Fenster-Body vollständig füllt.
   * Der Hintergrund ist derselbe wie im Fenster-Body: sonst scheint der Inhalt
   * unter der festgehefteten Zeile durch.
   *
   * Im schwebenden und im maximierten Fenster bleibt die Zeile bewusst im
   * Fluss: dort bedient die Titelleiste, die ohnehin außerhalb des Scrollers
   * liegt.
   */
  kontextZeileSticky: {
    position: "sticky",
    top: 0,
    zIndex: 20,
    backgroundColor: BRAND.bg,
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  kontextText: {
    // `minWidth: 0` ist Pflicht: als Flex-Kind schrumpft der Text sonst nicht
    // unter seine Inhaltsbreite und das Kürzen per Ellipse greift nie.
    minWidth: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 500,
    color: BRAND.muted,
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    marginTop: "4px",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "8px",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: { marginTop: "20px" },
  bottomBarFixed: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 15,
  },
  /** Eingerückt, solange das Verzeichnis links steht. */
  bottomBarFixedNebenSidebar: {
    left: `${SIDEBAR_WIDTH}px`,
  },
  // Summary
  summaryGrid: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  summaryCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  summaryHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    columnGap: "10px",
    marginBottom: "12px",
  },
  summaryTitle: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    color: BRAND.ink,
  },
  summaryEdit: {
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    cursor: "pointer",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    ...shorthands.border("0"),
    textDecorationLine: "underline",
  },
  rowFact: {
    display: "flex",
    justifyContent: "space-between",
    columnGap: "12px",
    paddingTop: "6px",
    paddingBottom: "6px",
    fontSize: "0.88rem",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  rowFactKey: { color: BRAND.muted },
  rowFactValue: { color: BRAND.ink },
});

// ── helpers ────────────────────────────────────────────────────────────

const newId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? (crypto as { randomUUID: () => string }).randomUUID()
    : `g-${Math.random().toString(36).slice(2)}`;

const initialGebaeude = (label = "Hauptgebäude"): Gebaeude => ({
  id: newId(),
  label,
  answers: {},
});

/**
 * Der EINE Eingang für fremde Antwort-Bags — aus dem Backend (Bearbeiten) wie
 * aus einem gespeicherten Entwurf.
 *
 * Zwei Schritte, beide idempotent: strukturierte Merkmale zurückparsen (sie
 * kommen als JSON-String, weil `characteristicsToAnswers` nicht parst) und
 * umbenannte Schlüssel auf die heutigen ziehen. Ohne den zweiten Schritt öffnete
 * ein vor dem 19.08.2026 erfasstes Gebäude die Frage „Brandschutzkonzept"
 * unbeantwortet und verbärge das bereits hochgeladene Konzept.
 */
const uebernommeneAntworten = (
  answers: Record<string, unknown> | null | undefined,
): Record<string, unknown> =>
  migriereAusstattung(migriereAltAntworten(parseStructuredAnswers(answers)));

const isOilHeating = (answers: Record<string, unknown>): boolean => {
  const heizungen = (answers.heizungen as HeizungSelections | undefined) ?? {};
  return heizungen["Ölheizung"]?.selected === "true";
};

// ── component ──────────────────────────────────────────────────────────

export type RisikoImmobilieStepFormProps =
  | {
      chrome: "public";
      /**
       * `"auto"` (Default) — die gemessene Container-Breite entscheidet
       * zwischen dauerhaftem Verzeichnis und Menü-Knopf. `"none"` schaltet das
       * Verzeichnis ganz ab (z. B. `RisikoanalyseDetailFormPublic`, wo heute
       * keins existiert und auch keins auftauchen soll).
       */
      toc?: TocMode;
    }
  | {
      chrome: "embedded";
      toc?: TocMode;
      /**
       * Woher die Schale ihre Höhe bezieht.
       *
       * - `"rahmen"` (Default): der Rahmen gibt sie vor — der Fenster-Body im
       *   Backoffice, die Vollflächen-Seite in `NewRiskPage`. Die Maske füllt
       *   ihn aus und scrollt IN SICH; einziger Scrollport ist `main`.
       * - `"inhalt"`: es gibt keinen Rahmen mit definiter Höhe. Die Maske ist
       *   dann eine Karte in einer gewöhnlichen Dokumentseite — sie wächst mit
       *   ihrem Inhalt, gescrollt wird das Dokument.
       *
       * Explizit hereingereicht und bewusst NICHT geraten: `overflow` kann sich
       * nicht an den Rahmen anpassen. Stünde die Maske in einer Dokumentseite
       * trotzdem auf „rahmen", wäre jeder Vorfahr der Schritt-Leiste ein
       * Scroll-Container ohne scrollbaren Überhang — `position: sticky` fände
       * keinen Scrollport mehr und die Leiste würde beim Scrollen weglaufen.
       * Einziger Aufrufer heute: `RisikoanalyseDetailFormPublic`.
       *
       * `"inhalt"` setzt voraus, dass kein dauerhaftes Verzeichnis danebensteht
       * (dort `toc="none"`): das `<aside>` bezöge seine Höhe sonst ebenfalls
       * aus dem Layout und endete auf Inhaltshöhe.
       */
      hoehe?: "rahmen" | "inhalt";
      embeddedSubmitLabel?: string;
      /**
       * ID der Fenster-Instanz, in der das Formular läuft. Nur damit erscheinen
       * die Bedienelemente in der Kontextzeile — und nur damit wird der
       * WindowManager-Context überhaupt gelesen. `RisikoanalyseDetailFormPublic`
       * rendert dieselbe Maske ebenfalls mit `chrome="embedded"`, läuft aber
       * ohne Provider; ohne diese Prop bleibt es dort still.
       */
      windowId?: string;
      /**
       * Kunde, für den erfasst wird — erscheint in der Kontextzeile. Auf den
       * öffentlichen Wegen bleibt er leer: dort zeigt ihn die TopBar bereits.
       */
      kundenName?: string | null;
      /**
       * „Schließen" in der Kontextzeile. Führt auf denselben Weg wie der
       * frühere „Zurück"-Knopf der Kopfzeile — inklusive Rückfrage bei
       * ungespeicherten Eingaben.
       */
      onCloseRequest?: () => void;
      /**
       * Kontaktauswahl gegen den KMVS-Kontaktbestand freischalten. Nur der
       * authentifizierte Backoffice-Weg (`NewRiskPage`) setzt das:
       * `RisikoanalyseDetailFormPublic` rendert dieselbe Komponente ebenfalls
       * mit `chrome="embedded"`, ist aber unauthentifiziert — dort blieben
       * `GET/POST /contact` bei 401/403 hängen.
       */
      contactLookup?: boolean;
      /** Edit-Mode: vorausgefüllte globale Antworten (Adresse etc.). */
      initialGlobalAnswers?: Record<string, unknown>;
      /** Edit-Mode: vorausgefüllte Gebäude (i. d. R. genau ein Gebäude). */
      initialGebaeude?: Gebaeude[];
      /**
       * Upload-Verhalten der Datei-Felder. **Explizit** hereingereicht und
       * bewusst NICHT aus `chrome` abgeleitet: `RisikoanalyseDetailFormPublic`
       * rendert dieselbe Maske ebenfalls mit `chrome="embedded"`, ist aber der
       * unauthentifizierte Kundenweg — eine Ableitung löste dort 401-Uploads
       * aus. Ohne diese Prop bleibt es beim reinen Hinweistext.
       */
      uploads?: UploadMode;
      /**
       * Absenden dauerhaft sperren, weil der Aufrufer bereits gespeichert hat
       * und das Fenster nur noch offen steht (Datei-Nachlauf gemeldet). Ohne
       * diese Sperre wäre der Knopf nach dem `finally` in `handleSubmit`
       * wieder aktiv und ein zweiter Klick legte sämtliche Gebäude ein
       * ZWEITES Mal an.
       */
      submitLocked?: boolean;
      onSubmitEmbedded: (payload: {
        global: Record<string, unknown>;
        gebaeude: Gebaeude[];
        /**
         * Im Modus `buffer` vorgemerkte Dateien. Der Aufrufer lädt sie NACH
         * dem Anlegen hoch — vorher gibt es kein RiskObject, an dem sie hängen
         * könnten. Additiv: wer nur `{ global, gebaeude }` destrukturiert,
         * bleibt unverändert.
         */
        uploads: PendingUpload[];
      }) => Promise<void> | void;
      /**
       * Fires whenever global answers oder eines der Gebäude verändert wird.
       * Wird benutzt, um den Window-Titel mit der Adresse zu aktualisieren.
       */
      onAnswersChange?: (payload: {
        global: Record<string, unknown>;
        gebaeude: Gebaeude[];
      }) => void;
    };

const RisikoImmobilieStepForm = (props: RisikoImmobilieStepFormProps) => {
  const styles = useStyles();
  const { token } = useParams();
  const isPublic = props.chrome === "public";
  const isEmbedded = props.chrome === "embedded";
  /**
   * Kein Rahmen mit definiter Höhe — die Maske steht als Karte in einer
   * Dokumentseite. Siehe Prop `hoehe`.
   */
  const inhaltshoehe = props.chrome === "embedded" && props.hoehe === "inhalt";
  const contactLookup = props.chrome === "embedded" && props.contactLookup === true;
  // Kundenansprache auf beiden Token-Wegen; intern füllt ein Mitarbeiter für
  // den Kunden aus (Plan §0, E11).
  const ansprache: Ansprache = anspracheFor({
    chrome: props.chrome,
    contactLookup,
  });
  /**
   * Datei-Puffer. Der State liegt HIER und nicht im Provider, weil
   * `handleSubmit` die vorgemerkten Dateien braucht — den eigenen Context
   * könnte die Komponente nicht per Hook lesen. Öffentlich ist er hart
   * abgeschaltet: dort gibt es keine Session, jeder Upload liefe in 401.
   */
  /**
   * Kundenlink je Immobilie (10/2026): der Token hängt an einem bestehenden
   * Risiko. Dann lädt die Maske dessen Merkmale, speichert jede Änderung
   * direkt dorthin und nimmt Unterlagen über den Token an.
   */
  const [kundenlink, setKundenlink] = useState<KundenlinkImmobilie | null>(null);
  const [serverFehler, setServerFehler] = useState<string | null>(null);
  /** Zuletzt mit dem Server abgeglichener Stand — verhindert Leer-Speichern. */
  const serverStandRef = useRef<string | null>(null);
  const uploadMode: UploadMode =
    props.chrome === "embedded"
      ? (props.uploads ?? UPLOAD_DISABLED)
      : kundenlink && token
        ? { mode: "token", token }
        : UPLOAD_DISABLED;
  const uploadBuffer = useUploadBufferState(uploadMode);
  /** Siehe Prop `submitLocked` — nach erfolgreichem Speichern hart gesperrt. */
  const submitLocked = props.chrome === "embedded" && props.submitLocked === true;
  const draftKey = (t: string) => DRAFT_LS_PREFIX + t;

  const [loading, setLoading] = useState(isPublic);
  const [valid, setValid] = useState(!isPublic);
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [contactPrefill, setContactPrefill] = useState<ContactPrefill | null>(null);
  // Im embedded-Mode kann der Aufrufer initial-Werte für Edit liefern.
  const embeddedInitial = props.chrome === "embedded" ? props : null;
  // Strukturierte Merkmale (einheiten, heizungen, sicherheit_* …) kommen aus dem
  // Backend als JSON-String zurück, weil characteristicsToAnswers nicht parst.
  // Hier einmalig zurückparsen — und dabei umbenannte Schlüssel mitziehen,
  // siehe `uebernommeneAntworten`. Beides ist idempotent.
  // Das Land stand bis 10/2026 nur als Anzeige-Vorgabe im Feld („Deutschland"),
  // im Zustand fehlte es — gespeichert wurde also kein Land, außer man wählte
  // es aktiv um. Jetzt steht die Vorgabe im Zustand selbst.
  const [globalAnswers, setGlobalAnswers] = useState<Record<string, unknown>>(() => {
    const start = uebernommeneAntworten(embeddedInitial?.initialGlobalAnswers);
    return start.country ? start : { ...start, country: "Deutschland" };
  });
  const [gebaeude, setGebaeude] = useState<Gebaeude[]>(() =>
    embeddedInitial?.initialGebaeude && embeddedInitial.initialGebaeude.length > 0
      ? embeddedInitial.initialGebaeude.map((g) => ({
          ...g,
          answers: uebernommeneAntworten(g.answers),
        }))
      : [initialGebaeude()],
  );

  // Live an Eltern weitermelden — Skip beim allerersten Render damit
  // initialer Default-State nicht sofort als "User-Eingabe" interpretiert wird.
  const skipFirstAnswersRef = useRef(true);
  useEffect(() => {
    if (skipFirstAnswersRef.current) {
      skipFirstAnswersRef.current = false;
      return;
    }
    if (props.chrome === "embedded" && props.onAnswersChange) {
      props.onAnswersChange({ global: globalAnswers, gebaeude });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAnswers, gebaeude]);

  /**
   * Vorgemerkte Dateien sind ungespeicherte Eingaben — und sie überleben kein
   * Unmount (`File` ist nicht serialisierbar). Sie laufen deshalb über
   * dasselbe Dirty-Signal wie die Antworten (#1307), damit das Schließen des
   * Fensters die Rückfrage auslöst statt die Auswahl still zu verwerfen.
   */
  const pendingCount = uploadBuffer.pending.length;
  const skipFirstUploadsRef = useRef(true);
  useEffect(() => {
    if (skipFirstUploadsRef.current) {
      skipFirstUploadsRef.current = false;
      return;
    }
    if (props.chrome === "embedded" && props.onAnswersChange) {
      props.onAnswersChange({ global: globalAnswers, gebaeude });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingCount]);

  const [stepIdx, setStepIdx] = useState(0);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  /**
   * Dauerhaftes Verzeichnis oder Menü-Knopf? Das entscheidet die gemessene
   * Breite des Shell-Elements — nicht `chrome` und nicht der Viewport.
   * Vollseite/maximiertes Fenster liegen über der Schwelle, ein
   * wiederhergestelltes Fenster (1140 px) darunter.
   */
  const { layout: tocLayout, availableHeight } = useTocLayout(shellRef, props.toc ?? "auto");
  /**
   * Linker Rand der Schritt-Leiste in der Vollseite — GEMESSEN am rechten Rand
   * des Verzeichnisses, nicht aus der Sidebar-Breite gerechnet (siehe
   * `useTocLeistenOffset`).
   */
  const { ref: verzeichnisRef, offset: tocLeistenOffset } = useTocLeistenOffset();
  /**
   * Vollseite (Window-State `"page"`)? Dort löst sich die Schritt-Leiste aus
   * der Inhaltsspalte und läuft über die volle Breite (#1307) — sie liegt dann
   * auch über dem unteren Rand des Verzeichnisses.
   */
  const istVollseite = useIsPageChrome();
  const autosaveTimerRef = useRef<number | null>(null);
  const draftLoadedRef = useRef(!isPublic);

  // ── public load ─────────────────────────────────────────
  useEffect(() => {
    if (!isPublic) return;
    const run = async () => {
      if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        if (!res.ok) {
          setValid(false);
          return;
        }
        setValid(true);
        let mitImmobilie = false;
        try {
          const data = await res.json();
          const immobilie = alsKundenlinkImmobilie(data?.immobilie);
          if (immobilie) {
            // Bestehende Immobilie: Stand des Servers ist maßgeblich — ein
            // lokaler Entwurf aus einem früheren Besuch würde ihn sonst
            // überschreiben.
            mitImmobilie = true;
            const teile = teileImmobilienMerkmale(immobilie.answers);
            const global = uebernommeneAntworten(teile.global);
            const gebaeudeStart: Gebaeude = {
              ...initialGebaeude(teile.label || "Hauptgebäude"),
              answers: uebernommeneAntworten(teile.gebaeude),
            };
            const globalMitLand = global.country ? global : { ...global, country: "Deutschland" };
            setGlobalAnswers(globalMitLand);
            setGebaeude([gebaeudeStart]);
            serverStandRef.current = JSON.stringify({ g: globalMitLand, b: gebaeudeStart.answers });
            setKundenlink(immobilie);
          }
          const c = data?.contact;
          if (c) {
            setCompanyName(c.displayName || c.lastname || "");
            const prefill: ContactPrefill = {
              displayName: c.displayName ?? "",
              lastname: c.lastname ?? "",
              firstName: c.firstName ?? "",
              street: c.address?.street ?? "",
              postalCode: c.address?.postalCode ?? "",
              city: c.address?.city ?? "",
              country: c.address?.country ?? "",
              phone: c.phone ?? "",
              email: c.email ?? "",
            };
            setContactPrefill(prefill);
            if (!mitImmobilie) setGlobalAnswers((prev) => ({
              street: prefill.street,
              postalCode: prefill.postalCode,
              city: prefill.city,
              ...prev,
              // Die Vorgabe „Deutschland" aus dem Startzustand darf ein im
              // Kontakt gepflegtes Land nicht verdrängen.
              country: prefill.country || prev.country || "Deutschland",
            }));
          }
        } catch {}

        // Erst der aktuelle Entwurf; sonst einmalig der jüngste Entwurf aus einer
        // älteren Schrittkette. Die Antworten bleiben in jedem Fall gültig, nur
        // der gespeicherte `stepIdx` zeigt in die alte Kette und wird deshalb
        // verworfen. Die Alt-Einträge werden danach aufgeräumt.
        const draftRaw = mitImmobilie ? null : window.localStorage.getItem(draftKey(token));
        const legacyRaw = draftRaw
          ? null
          : LEGACY_DRAFT_LS_PREFIXES.map((p) =>
              window.localStorage.getItem(p + token)
            ).find((v) => v != null) ?? null;
        const roh = mitImmobilie ? null : (draftRaw ?? legacyRaw);
        if (roh) {
          try {
            const parsed = JSON.parse(roh);
            if (parsed && typeof parsed === "object") {
              // Auch der Entwurf läuft über `uebernommeneAntworten`: er kann
              // im Browser des Kunden älter sein als der Umbau, der einen
              // Antwort-Schlüssel umbenannt hat.
              if (parsed.globalAnswers) {
                setGlobalAnswers(uebernommeneAntworten(parsed.globalAnswers));
              }
              if (Array.isArray(parsed.gebaeude) && parsed.gebaeude.length > 0) {
                setGebaeude(
                  (parsed.gebaeude as Gebaeude[]).map((g) => ({
                    ...g,
                    answers: uebernommeneAntworten(g.answers),
                  })),
                );
              }
              if (!legacyRaw && typeof parsed.stepIdx === "number") {
                setStepIdx(parsed.stepIdx);
              }
              if (typeof parsed.savedAt === "string") setDraftSavedAt(parsed.savedAt);
            }
          } catch {}
        }
        try {
          for (const p of LEGACY_DRAFT_LS_PREFIXES) {
            window.localStorage.removeItem(p + token);
          }
        } catch {}
        draftLoadedRef.current = true;
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token, isPublic]);

  /**
   * Kundenlink: den Gesamtstand ins Risiko schreiben. `final` schließt den
   * Link (Absenden). Liefert `true` bei Erfolg.
   */
  const speichereKundenlink = async (final: boolean): Promise<boolean> => {
    if (!token) return false;
    const stand = JSON.stringify({ g: globalAnswers, b: gebaeude[0]?.answers ?? {} });
    try {
      const res = await fetch(apiUrl(`public-form/${token}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          // Nur das erste Gebäude: der Link gehört zu GENAU einem Risiko.
          global: globalAnswers,
          gebaeude: gebaeude.slice(0, 1),
          version: "immobilie-step-v1",
          ...(final ? { final: true } : {}),
        }),
      });
      if (!res.ok) {
        setServerFehler(
          res.status === 404
            ? "Der Link ist abgelaufen — Ihre letzten Änderungen konnten nicht gespeichert werden."
            : "Speichern fehlgeschlagen — bitte prüfen Sie Ihre Verbindung.",
        );
        return false;
      }
      serverStandRef.current = stand;
      setServerFehler(null);
      setDraftSavedAt(new Date().toISOString());
      return true;
    } catch {
      setServerFehler("Speichern fehlgeschlagen — bitte prüfen Sie Ihre Verbindung.");
      return false;
    }
  };

  // ── Kundenlink: Änderungen gesammelt ins Risiko schreiben ──
  useEffect(() => {
    if (!isPublic || !kundenlink || !token || submitOk) return;
    const stand = JSON.stringify({ g: globalAnswers, b: gebaeude[0]?.answers ?? {} });
    if (stand === serverStandRef.current) return;
    const timer = window.setTimeout(() => {
      void speichereKundenlink(false);
    }, KUNDENLINK_SPEICHER_MS);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalAnswers, gebaeude, kundenlink, token, isPublic, submitOk]);

  // ── debounced autosave (public only) ─────────────────────
  useEffect(() => {
    if (!isPublic || !draftLoadedRef.current || !token || kundenlink) return;
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      const payload = {
        globalAnswers,
        gebaeude,
        stepIdx,
        savedAt: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(draftKey(token), JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } catch {}
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, [globalAnswers, gebaeude, stepIdx, token, isPublic, kundenlink]);

  // ── helpers ─────────────────────────────────────────────
  const setGlobalField = (key: string, value: unknown) => {
    setGlobalAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const updateGebaeude = (id: string, patch: Partial<Gebaeude>) => {
    setGebaeude((prev) =>
      prev.map((g) => (g.id === id ? { ...g, ...patch } : g))
    );
  };

  const setGebaeudeAnswer = (id: string, key: string, value: unknown) => {
    setGebaeude((prev) =>
      prev.map((g) =>
        g.id === id ? { ...g, answers: { ...g.answers, [key]: value } } : g
      )
    );
  };

  /**
   * Mehrere Antworten in EINEM Zustandsschritt. Zwei aufeinanderfolgende
   * `setGebaeudeAnswer`-Aufrufe aus demselben Ereignis rechnen beide auf dem
   * `answers` desselben Renders — der zweite würde den ersten still
   * überschreiben. Genau das passiert bei den Stellplätzen, wo Auswahl
   * (`stellplatzArten`) und Details (`stellplatzDetails`) gemeinsam wandern.
   */
  const setGebaeudeAnswers = (id: string, patch: Record<string, unknown>) => {
    setGebaeude((prev) =>
      prev.map((g) =>
        g.id === id ? { ...g, answers: { ...g.answers, ...patch } } : g
      )
    );
  };

  const addGebaeude = () => {
    const next: Gebaeude = initialGebaeude(
      `Gebäude ${gebaeude.length + 1}`
    );
    setGebaeude([...gebaeude, next]);
  };

  const removeGebaeude = (id: string) => {
    if (gebaeude.length <= 1) return;
    setGebaeude(gebaeude.filter((g) => g.id !== id));
    // Vorgemerkte Dateien dieses Gebäudes mit verwerfen — analog zu
    // `onUnitRemoved` bei den Einheiten. Blieben sie im Puffer, fänden sie im
    // Nachlauf kein Ziel mehr und lägen am Ende am falschen Gebäude.
    uploadBuffer.dropScope(id);
  };

  // ── Schrittkette & Inhaltsverzeichnis ──────────────────
  // Beides leitet sich aus derselben Gliederung ab (E14, `gliederung.ts`),
  // damit Kette und Verzeichnis nicht auseinanderlaufen können.
  const steps = useMemo(() => buildImmoSteps(gebaeude), [gebaeude]);
  const totalSteps = steps.length;

  /**
   * Das Verzeichnis zeigt die Abschnitte GENAU EINES Gebäudes. `SectionDef`
   * kennt nur zwei Ebenen (Abschnitt → Schritt) und die neue Gliederung belegt
   * beide — das Gebäude wird deshalb oberhalb der Liste gewählt, statt eine
   * dritte Ebene zu erfinden. Nebeneffekt: die Liste bleibt unabhängig von der
   * Gebäudezahl kurz (11 Einträge statt 1 + n × 19 + 1).
   */
  const [manuellGewaehltesGebaeude, setManuellGewaehltesGebaeude] = useState<string | null>(null);
  const aktuellerStep: ImmoStep | undefined = steps[stepIdx];
  const stepGebaeudeId =
    aktuellerStep && "gebaeudeId" in aktuellerStep ? aktuellerStep.gebaeudeId : null;
  /**
   * ABGELEITET, nicht per Effekt nachgezogen. Mit State+Effekt wäre beim
   * Blättern über eine Gebäudegrenze genau ein Render lang kein Abschnitt
   * gefunden worden (`sectionIdx === -1`) — Abschnittspille und Fußzeile wären
   * kurz auf „Anschrift" samt deren Farbe zurückgefallen.
   *
   * Vorrang hat immer das Gebäude des aktuellen Schritts; die manuelle Wahl
   * greift nur auf den gebäudelosen Schritten (Anschrift, Zusammenfassung) und
   * verfällt, sobald das Gebäude gelöscht wurde.
   */
  const tocGebaeudeId =
    stepGebaeudeId ??
    (manuellGewaehltesGebaeude &&
    gebaeude.some((g) => g.id === manuellGewaehltesGebaeude)
      ? manuellGewaehltesGebaeude
      : gebaeude[0]?.id ?? "");

  const sections = useMemo(
    () => buildImmoSections({ steps, gebaeude, activeGebaeudeId: tocGebaeudeId }),
    [steps, gebaeude, tocGebaeudeId],
  );

  // Position im Verzeichnis über den Schritt-SCHLÜSSEL statt über
  // Index-Arithmetik: die Liste enthält nur noch einen Teil der Kette, ein
  // laufender Zähler über `subSteps.length` ginge daneben.
  const { sectionIdx, subStepIdx } = useMemo(
    () => locateInSections(sections, steps[stepIdx]?.key),
    [sections, steps, stepIdx],
  );

  // Clamp
  useEffect(() => {
    if (stepIdx >= totalSteps) setStepIdx(totalSteps - 1);
  }, [totalSteps, stepIdx]);

  // One-time prefill of Eigentümer fields from token contact, per Gebäude.
  // Nicht beim Kundenlink einer bestehenden Immobilie: dort stehen die
  // Eigentümerdaten im Risiko, und eine Vorbelegung würde schon beim bloßen
  // Öffnen ein Speichern auslösen.
  useEffect(() => {
    if (!contactPrefill || kundenlink) return;
    setGebaeude((prev) =>
      prev.map((g) => {
        const a = g.answers;
        if (a.eigentuemerName != null || a.eigentuemerStrasse != null) return g;
        return {
          ...g,
          answers: {
            ...a,
            eigentuemerName: contactPrefill.displayName,
            eigentuemerStrasse: contactPrefill.street,
            eigentuemerPlz: contactPrefill.postalCode,
            eigentuemerOrt: contactPrefill.city,
          },
        };
      })
    );
    // We intentionally only run when contactPrefill changes (initial token load)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactPrefill]);

  // ── progress (rough — count answered keys per Gebäude) ──
  const progress = useMemo(() => {
    let total = 0;
    let answered = 0;
    // Anschrift: 4 fields
    total += 4;
    for (const k of ["street", "postalCode", "city", "country"]) {
      if (typeof globalAnswers[k] === "string" && (globalAnswers[k] as string).trim() !== "")
        answered += 1;
    }
    for (const g of gebaeude) {
      const a = g.answers;
      // Die Zahl der zählbaren Felder war bis hierher als „30" doppelt geführt
      // und lief mit jedem neuen Schritt aus dem Tritt — jetzt aus der Liste
      // selbst abgeleitet.
      const keysToCount = [
        "kategorie",
        "typ",
        "geschosseUeber",
        "geschosseUnter",
        "stellung",
        "bauartklasse",
        "baujahr",
        "gesamtflaeche",
        // Ja/Nein-Gates der Bauart-Seite: ein bewusstes „nein" ist eine
        // Antwort. Die Listen dahinter (`schaeden`, `stellplatzArten`) zählen
        // bewusst NICHT mit — sonst erreichte ein Gebäude ohne Schäden und
        // ohne Stellplätze nie 100 %.
        "schaedenJa",
        "stellplatzVorhanden",
        "eigennutzung",
        "dachTyp",
        // Beim Ein-/Zweifamilienhaus steht die Anzahl fest; beantwortet wird
        // dort die Einliegerwohnung.
        profileForType(a.kategorie as string | undefined, a.typ as string | undefined).countLocked
          ? "einliegerwohnung"
          : "anzahlEinheiten",
        ...(wohnflaecheGefragt(a) ? ["wohnflaeche"] : []),
        "saniert",
        "sanierungGeplant",
        "leerstand",
        // Die Spezifizierung gibt es nur bei Gewerbe/Mischnutzung — beim
        // Wohnhaus zählte sie bis 10/2026 trotzdem mit, 100 % war unerreichbar.
        ...(istGewerblich(g) ? ["branchen"] : []),
        "kunst",
        "aussenanlagen",
        "bauausfuehrungBewertung",
        "denkmalschutz",
        // Nur die fünf Hauptanlagen zählen mit: sie werden ausdrücklich mit
        // Ja oder Nein beantwortet. Die 23 angekreuzten Katalog-Anlagen
        // dürfen NICHT mitzählen — für eine nicht gewählte Anlage entsteht
        // bewusst gar kein Merkmal, ein Gebäude ohne Löschanlagen erreichte
        // sonst nie 100 %.
        // Weiche Wohnen ↔ Gewerbe: ausgeblendete Fragen zählen nicht mit.
        ...["bma", "ema", "rwa", "sprinkler", "blitzschutz"]
          .filter((k) => anlageSichtbar(k, a))
          .map(anlagenAnswerKey),
        "brandschutzDokumentVorhanden",
        ...(notstromSichtbar(a) ? ["notstrom"] : []),
        // Die drei Ja/Nein-Tore der Schadenprävention — ein bewusstes „nein"
        // ist eine Antwort. Die Folgefelder zählen bewusst nicht mit.
        "praeventionLeitungswasser",
        "praeventionUeberschwemmung",
        "praeventionSonstige",
        "heizungen",
        "umwelt",
        // Der reale Answer-Key des Anbauten-Gates heißt `anbautenJa`;
        // „anbauten" zählte hier nie etwas mit.
        "anbautenJa",
        "eigentuemerName",
        "eigentuemerAnteilProzent",
        // Ja/Nein-Gates der neuen Beteiligten-Schritte — ein bewusstes „nein"
        // ist eine Antwort und zählt als beantwortet.
        "miteigentuemerJa",
        "niessbrauch",
        "erbbaurecht",
        "darlehenJa",
        "gutachten",
        "hvBeauftragt",
        "vermietung",
        "verpachtung",
        "betreiberAbweichend",
        "sonstigeNutzungJa",
        "facilityBeauftragt",
        "weitereBeteiligteJa",
        "versicherungsbedarf",
      ];
      total += keysToCount.length;
      for (const k of keysToCount) {
        const v = a[k];
        if (v != null && v !== "" && (typeof v !== "object" || Object.keys(v as object).length > 0)) {
          answered += 1;
        }
      }
    }
    return { total, answered };
  }, [globalAnswers, gebaeude]);
  const overallFraction = progress.total === 0 ? 0 : progress.answered / progress.total;
  const stepFraction = (stepIdx + 1) / totalSteps;

  /**
   * Nach JEDEM Schrittwechsel steht der Inhalt oben — egal ob Weiter, Zurück,
   * Klick im Verzeichnis oder Gebäude-Wechsel.
   *
   * Bewusst als Effekt und nicht in den Klick-Handlern: `setStepIdx` rendert
   * erst danach, die Handler scrollten also noch den ALTEN Inhalt und die
   * begonnene Animation wurde vom folgenden Render abgeschnitten. Der Effekt
   * läuft nach dem Commit des neuen Schritts.
   *
   * Drei Container, weil das Formular in drei Rahmen läuft: `main` (öffentliche
   * Vollseite), Fenster/`window` (`scrollFormToTop`) und — im schwebenden
   * Fenster — ein `overflow: auto`-`<div>` zwischen beiden.
   *
   * Ohne Animation (`"auto"`): beim Schrittwechsel ist der Inhalt vollständig
   * ausgetauscht, es gibt also nichts, woran ein Gleiten vorbeiführen würde.
   * Eine laufende Smooth-Animation wird zudem von jedem konkurrierenden Scroll
   * still abgebrochen (Scroll-Anchoring, wenn der neue Schritt kürzer ist; ein
   * `focus()` im neuen Schritt) — dann bliebe der Sprung mal aus und mal nicht.
   */
  const ersterSchrittRender = useRef(true);
  useEffect(() => {
    // Beim ersten Rendern gibt es nichts zurückzusetzen; ein Entwurf, der auf
    // einem späteren Schritt wieder aufsetzt, darf die Seite nicht anspringen.
    if (ersterSchrittRender.current) {
      ersterSchrittRender.current = false;
      return;
    }
    const el = mainRef.current;
    // `Element.scrollTo` fehlt in jsdom — und ein Sprung darf nie werfen.
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: "auto" });
    }
    scrollFormToTop("auto");
    scrollScrollableAncestorsToTop(shellRef.current, "auto");
  }, [stepIdx]);

  const goPrev = () => setStepIdx((s) => Math.max(0, s - 1));
  const goNext = () => setStepIdx((s) => Math.min(totalSteps - 1, s + 1));
  const jumpToStepKey = (key: string) => {
    const idx = steps.findIndex((s) => s.key === key);
    if (idx < 0) return;
    setStepIdx(idx);
  };

  // Verzeichnis-Einträge tragen den Schritt-SCHLÜSSEL, nicht eine Position in
  // der Kette — deshalb wird hier über den Schlüssel aufgelöst.
  const handleSelect = (sIdx: number, ssIdx: number) => {
    const key = sections[sIdx]?.subSteps[ssIdx]?.key;
    if (key) jumpToStepKey(key);
  };

  /** Klick auf die Abschnittszeile → deren Kopf-Schritt. */
  const handleSectionSelect = (sIdx: number) => {
    const key = sections[sIdx]?.key;
    if (key) jumpToStepKey(key);
  };

  /** Umschalter über dem Verzeichnis: springt an den Anfang des Gebäudes. */
  const handleGebaeudeWechsel = (id: string) => {
    setManuellGewaehltesGebaeude(id);
    const idx = steps.findIndex((st) => "gebaeudeId" in st && st.gebaeudeId === id);
    if (idx >= 0) setStepIdx(idx);
  };

  const handleSubmit = async () => {
    // Wiedereintritts-Schutz: `submitting` sperrt zwar den Knopf, aber der
    // Aufrufer kann `handleSubmit` auch sonst erreichen — und `submitLocked`
    // bleibt nach erfolgreichem Speichern bestehen, während das Fenster für
    // eine Upload-Meldung noch offen steht.
    if (submitting || submitLocked) return;
    if (isEmbedded) {
      const onSubmit = (props as Extract<RisikoImmobilieStepFormProps, { chrome: "embedded" }>).onSubmitEmbedded;
      if (!onSubmit) return;
      setSubmitting(true);
      try {
        await onSubmit({
          global: globalAnswers,
          gebaeude,
          uploads: uploadBuffer.allPending(),
        });
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!token) return;
    setSubmitting(true);
    if (kundenlink) {
      try {
        if (await speichereKundenlink(true)) setSubmitOk(true);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    try {
      const res = await fetch(apiUrl(`public-form/${token}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          global: globalAnswers,
          gebaeude,
          version: "immobilie-step-v1",
        }),
      });
      if (res.ok) {
        try {
          window.localStorage.removeItem(draftKey(token));
        } catch {}
        setSubmitOk(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isPublic && loading) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Formular wird geladen…</div>
        </div>
      </div>
    );
  }
  if (isPublic && !valid) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Der Link ist ungültig oder abgelaufen.</div>
        </div>
      </div>
    );
  }
  if (isPublic && submitOk) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <h2 className={styles.pageStateTitle}>Vielen Dank</h2>
          <p className={styles.pageStateBody}>
            Ihre Immobiliendaten wurden übermittelt. Wir melden uns mit den nächsten Schritten.
          </p>
        </div>
      </div>
    );
  }

  // ── current step rendering ────────────────────────────
  const step = steps[stepIdx];
  // `locateInSections` liefert -1, wenn der aktuelle Schritt zu einem Gebäude
  // gehört, das das Verzeichnis gerade nicht zeigt. Der Rückfall hält
  // Abschnittspille und Fußzeile beschriftet, statt sie leer zu lassen.
  const section = sections[sectionIdx] ?? sections[0];
  const heroKind: ImmoStepKind = step.kind as ImmoStepKind;
  const subStepTitle =
    step.kind === "summary" ? "Übersicht" : step.kind === "anschrift" ? "Risikoanschrift" : titleForKind(step.kind);
  const stepDescription = descriptionForKind(step.kind, ansprache);

  const currentG: Gebaeude | undefined =
    step.kind === "anschrift" || step.kind === "summary"
      ? undefined
      : gebaeude.find((g) => g.id === (step as Extract<ImmoStep, { gebaeudeId: string }>).gebaeudeId);

  const renderBody = (): JSX.Element => {
    if (step.kind === "anschrift") return renderAnschrift();
    if (step.kind === "summary") return renderSummary();
    if (!currentG) return <div />;
    switch (step.kind) {
      case "gebaeudeart":
        return renderGebaeudeart(currentG);
      case "spezifizierung":
        return renderSpezifizierung(currentG);
      case "dach":
        return renderDach(currentG);
      case "einheiten_anzahl":
        return renderEinheiten(currentG);
      case "branchen":
        return renderBranchen(currentG);
      case "aussenanlagen":
        return renderAussenanlagen(currentG);
      case "bauausfuehrung":
        return renderBauausfuehrung(currentG);
      case "sicherheit":
        return renderSicherheit(currentG);
      case "praevention":
        return renderPraevention(currentG);
      case "energie":
        return renderEnergie(currentG);
      case "umwelt":
        return renderUmwelt(currentG);
      case "anbauten":
        return renderAnbauten(currentG);
      case "eigentum":
        return renderEigentum(currentG);
      case "beteiligte":
        return renderBeteiligte(currentG);
      case "gutachten":
        return renderGutachten(currentG);
      case "finanzierung":
        return renderFinanzierung(currentG);
      case "versicherungsbedarf":
        return renderVersicherungsbedarf(currentG);
      case "unterlagen":
        return renderUnterlagen(currentG);
      default: {
        /*
          Bis 19.08.2026 stand hier `return <div />` — eine Schritt-Art ohne
          Fall ergab damit eine LEERE SEITE, ohne Fehler an irgendeiner Stelle.
          Jetzt ist der Zweig exhaustiv: `step` ist hier `never`, ein neuer oder
          umbenannter Schritt ohne Fall bricht also den Type-Check. Der Rückfall
          zur Laufzeit sagt außerdem, WAS fehlt, statt nichts zu zeigen.
        */
        const unbehandelt: never = step;
        return (
          <div className={styles.questions}>
            Für diesen Schritt ist keine Ansicht hinterlegt (
            {String((unbehandelt as ImmoStep).kind)}).
          </div>
        );
      }
    }
  };

  // ── per-step renderers ───────────────────────────────
  function renderAnschrift(): JSX.Element {
    return (
      <div className={styles.questions}>
        <AnschriftBlock
          street={(globalAnswers.street as string) ?? ""}
          postalCode={(globalAnswers.postalCode as string) ?? ""}
          city={(globalAnswers.city as string) ?? ""}
          country={(globalAnswers.country as string) ?? ""}
          onChange={(k, v) => setGlobalField(k, v)}
          // Entscheidung des Fachbereichs (18.08.2026, E4): die 3D-Schrägansicht
          // steht beiden Wegen zu — intern wie im öffentlichen Kundenformular.
          // Diese Komponente bedient beide (chrome="public" | "embedded").
          geocode
        />
        {/* Kundenlink für den Berater — gleich im ersten Schritt, damit er den
            Link erzeugen kann, ohne die Erfassung zu verlassen. Er gehört zu
            genau EINEM gespeicherten Risiko (Modus `immediate` = Bearbeiten). */}
        {uploadMode.mode === "immediate" && (
          <div style={{ marginTop: 16 }}>
            <KundenlinkKarte riskObjectId={uploadMode.riskObjectId} />
          </div>
        )}
        {uploadMode.mode === "buffer" && (
          <p style={{ fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", color: BRAND.muted, margin: "12px 0 0" }}>
            Kundenlink: Sobald die Immobilie gespeichert ist, können Sie hier einen Link erzeugen,
            über den der Kunde die Angaben selbst ergänzt.
          </p>
        )}
        <UploadField
          scope={GLOBAL_SCOPE}
          slotKey="objektfoto"
          label="Foto / Skizze des Objekts"
          accept="image/*,application/pdf,.heic,.heif"
          ansprache={ansprache}
        />
        {/* Die Frage „Gibt es mehrere Gebäude auf dem Grundstück?" stand hier
            bis 10/2026 und doppelte die Anbauten-Frage („Für mich nicht
            zweimal gefragt werden", Fachabstimmung 31.08.2026). Weitere
            Gebäude entstehen jetzt aus dem Schritt „Anbauten"; die Übersicht
            bleibt hier, sobald es mehr als eins gibt. */}
        {(globalAnswers.hasMultiple === "ja" || gebaeude.length > 1) && (
          <div style={{ marginTop: 12 }}>
            <GebaeudeRegistry
              gebaeude={gebaeude}
              onRename={(id, label) => updateGebaeude(id, { label })}
              onAdd={addGebaeude}
              onRemove={removeGebaeude}
            />
          </div>
        )}
      </div>
    );
  }

  function renderGebaeudeart(g: Gebaeude): JSX.Element {
    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 24 }}>
        <div>
          <h3 style={headingStyle()}>Welche Kategorie hat das Gebäude?</h3>
          <KategoriePicker
            value={(g.answers.kategorie as string) ?? ""}
            onChange={(v) => {
              setGebaeudeAnswer(g.id, "kategorie", v);
              setGebaeudeAnswer(g.id, "typ", "");
            }}
          />
        </div>
        <div>
          <h3 style={headingStyle()}>Welcher Gebäudetyp passt am besten?</h3>
          <TypPicker
            kategorie={(g.answers.kategorie as string) ?? ""}
            value={(g.answers.typ as string) ?? ""}
            onChange={(v) => setGebaeudeAnswer(g.id, "typ", v)}
          />
        </div>
      </div>
    );
  }

  function renderSpezifizierung(g: Gebaeude): JSX.Element {
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    const a = g.answers as Answers;
    // Geschosse, Stellung und Baujahr haben eigene Bedienelemente (Zähler,
    // Bildauswahl, große Zahleneingabe) und hängen deshalb in einer FELDLOSEN
    // `QuestionDef` im children-Slot: Überschrift, Abstand und gestrichelter
    // Trenner bleiben damit identisch zum Rest des Schritts, die Reihenfolge
    // der Fragen ebenso.
    // Sanierungen: mehrere Einträge statt eines Satzes Einzelfelder, dazu die
    // geplante Sanierung (Fachabstimmung 31.08.2026). Die früheren Felder
    // `sanierungUmfang/Was/Wann` erscheinen als erster Listeneintrag und
    // werden beim ersten Bearbeiten der Liste geleert — leere Werte verwirft
    // der Speicherpfad, die Angabe lebt dann nur noch in `sanierungen`.
    const sanierungen = sanierungenAusAntworten(a);
    const setSanierungen = (next: SanierungEintrag[]) =>
      setGebaeudeAnswers(g.id, {
        sanierungen: next,
        ...Object.fromEntries(SANIERUNG_ALT_SCHLUESSEL.map((k) => [k, ""])),
      });
    const geplanteSanierung: QuestionDef = {
      question: "Ist eine Sanierung geplant?",
      field: { key: "sanierungGeplant", type: "yesno" },
      showFollowUpWhen: { key: "sanierungGeplant", equals: "ja" },
      followUpFields: [
        { key: "sanierungGeplantWas", label: "Was soll saniert werden?", type: "text" },
        {
          key: "sanierungGeplantUmfang",
          label: "Umfang",
          type: "select",
          options: SANIERUNG_UMFANG_OPTIONEN.map((o) => ({ value: o.value, label: o.label })),
        },
        { key: "sanierungGeplantAb", label: "Ab wann?", type: "date" },
        { key: "sanierungGeplantKosten", label: "Voraussichtliche Kosten (€)", type: "number" },
      ],
    };

    // Schadenhistorie (xlsx Z. 7.4b) und Stellplätze je Art (Z. 7.7) sind
    // eigene Blöcke unterhalb der Fragenliste — beide brauchen mehr als ein
    // Feld je Zeile bzw. je Art und passen deshalb nicht in eine `QuestionDef`.
    const schaeden = asSchadenListe(a.schaeden);
    const stellplatzArten = asStellplatzArten(a.stellplatzArten);
    const stellplatzDetails = asStellplatzDetails(a.stellplatzDetails);
    // Bestandsdaten kennen das Gate noch nicht. Wo bereits Stellplätze erfasst
    // sind, steht es deshalb auf „ja" — sonst verschwänden die vorhandenen
    // Angaben beim Öffnen hinter einem unbeantworteten Ja/Nein. Geschrieben
    // wird dabei nichts; erst eine echte Antwort landet im Merkmal.
    const stellplatzBestand =
      stellplatzArten.length > 0 ||
      Object.keys(stellplatzDetails).length > 0 ||
      (typeof a.stellplatzAnzahl === "string" && a.stellplatzAnzahl !== "") ||
      (typeof a.stellplatzLage === "string" && a.stellplatzLage !== "");
    const stellplatzGate =
      (a.stellplatzVorhanden as string | undefined) ||
      (stellplatzBestand ? "ja" : "");

    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        <div>
          <h3 style={headingStyle()}>Welche Bauartklasse trifft zu?</h3>
          <BauartklassePicker
            value={(a.bauartklasse as string) ?? ""}
            onChange={(v) => onChange("bauartklasse", v)}
          />
        </div>

        <QuestionBlock
          question={{ question: "Wie viele Geschosse hat das Gebäude?" }}
          answers={a}
          onChange={onChange}
        >
          {/* Nebeneinander statt untereinander (Auftraggeber, 19.08.2026) — der
              Umbruch folgt der Container-Breite, siehe `geschossPaarLayout`. */}
          <div style={geschossPaarStil} data-testid={GESCHOSS_PAAR_TESTID}>
            {/* Die Vorschläge 3 und 0 stehen nur als Platzhalter im Feld —
                geschrieben wird erst, wenn jemand tippt oder „+"/„−" drückt. */}
            <GeschossStepper
              variante="ueber"
              label="Über Erdgleiche"
              vorschlag={3}
              value={a.geschosseUeber}
              onChange={(v) => onChange("geschosseUeber", v)}
            />
            <GeschossStepper
              variante="unter"
              label="Unter Erdgleiche (Keller)"
              vorschlag={0}
              value={a.geschosseUnter}
              onChange={(v) => onChange("geschosseUnter", v)}
            />
          </div>
          {/* Kellerfläche nur bei Gewerbe/Mischnutzung: beim Wohnkeller ist
              sie nicht nötig, als Lager oder gewerblich genutzte Fläche schon
              (Fachabstimmung 31.08.2026). */}
          {istGewerblich(g) && Number(a.geschosseUnter ?? 0) > 0 && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12, marginTop: 16 }}>
              <h4 style={{ ...headingStyle(), fontSize: "0.95rem", margin: 0 }}>
                Kellerfläche (m²)
              </h4>
              <GrossZahlEingabe
                value={a.kellerflaeche}
                onChange={(v) => onChange("kellerflaeche", v)}
                ariaLabel="Kellerfläche (m²)"
                name="kellerflaeche"
                einheit="m²"
                dezimal
              />
              <QuestionField
                field={{
                  key: "kellerNutzung",
                  label: "Wie wird der Keller genutzt?",
                  type: "select",
                  options: KELLER_NUTZUNG.map((n) => ({ value: n, label: n })),
                }}
                answers={a}
                onChange={onChange}
              />
            </div>
          )}
        </QuestionBlock>

        <QuestionBlock
          question={{ question: "Wie steht das Gebäude?" }}
          answers={a}
          onChange={onChange}
        >
          <StellungPicker
            value={(a.stellung as string) ?? ""}
            onChange={(v) => onChange("stellung", v)}
          />
          {/*
            „Sonstiges" allein sagt nichts — deshalb die nähere Beschreibung
            (Auftraggeber, 19.08.2026). Sie hängt bewusst HIER im Aufrufer und
            nicht in `StellungPicker`: der Picker bleibt damit ein Wert rein,
            ein Wert raus, sein Katalog (`STELLUNG_OPTIONS`) unverändert.

            Ein einmal getippter Text bleibt beim Umschalten auf „freistehend"
            oder „angebaut" im State stehen und wird auch mitgespeichert. Das
            ist Absicht: ein Fehlklick auf eine andere Karte darf den Satz nicht
            vernichten, und wer zurückschaltet, findet ihn wieder. Ein
            bedingtes Verwerfen beim Speichern gibt es in diesem Formular
            nirgends — es müsste an BEIDEN Absendewegen (öffentlich und
            eingebettet über `NewRiskPage`) hängen und verhielte sich sonst
            unterschiedlich. Ein Restschlüssel neben „freistehend" ist dagegen
            folgenlos und hat Vorbilder (`stellplatzLage`/`stellplatzAnzahl`).
          */}
          {a.stellung === "sonstiges" && (
            <div style={{ marginTop: 12 }}>
              <QuestionField
                field={{
                  key: "stellungBeschreibung",
                  label: "Nähere Beschreibung",
                  type: "text",
                  placeholder: "z. B. Reihenendhaus mit Grenzgarage",
                }}
                answers={a}
                onChange={onChange}
              />
            </div>
          )}
        </QuestionBlock>

        <QuestionBlock
          question={{ question: "In welchem Jahr wurde das Gebäude erbaut?" }}
          answers={a}
          onChange={onChange}
        >
          <GrossZahlEingabe
            value={a.baujahr}
            onChange={(v) => onChange("baujahr", v)}
            ariaLabel="Baujahr des Gebäudes"
            platzhalter="JJJJ"
            maxLen={4}
          />
        </QuestionBlock>

        <QuestionBlock
          question={{
            question: "Wurde das Objekt saniert?",
            field: { key: "saniert", type: "yesno" },
          }}
          answers={a}
          onChange={onChange}
        >
          {a.saniert === "ja" && (
            <SanierungListe entries={sanierungen} onChange={setSanierungen} />
          )}
        </QuestionBlock>

        <QuestionBlock question={geplanteSanierung} answers={a} onChange={onChange} />

        <QuestionBlock
          question={{
            question: "Gab es in den letzten Jahren Schäden in oder am Objekt?",
            field: { key: "schaedenJa", type: "yesno" },
          }}
          answers={a}
          onChange={onChange}
        >
          {a.schaedenJa === "ja" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <SchadenListe
                entries={schaeden}
                onChange={(next) => onChange("schaeden", next)}
                intro={immoText("schaedenIntro", ansprache)}
              />
              {/* Alt-Key aus der Zeit des einen Freitextfelds: vorhandene Texte
                  bleiben sichtbar und werden nicht überschrieben. */}
              <QuestionBlock
                question={{
                  question: "Ergänzende Bemerkungen zu den Schäden (optional)",
                  field: {
                    key: "schaedenDetails",
                    type: "textarea",
                    placeholder: "z. B. Zusammenhänge, Sanierungen nach dem Schaden",
                  },
                }}
                answers={a}
                onChange={onChange}
              />
            </div>
          )}
        </QuestionBlock>

        <GesamtflaecheBlock
          einheiten={a.einheiten}
          value={a.gesamtflaeche}
          onChange={(v) => onChange("gesamtflaeche", v)}
        />

        {/* Wohnfläche als eigenes Pflichtfeld — „die Gesamtfläche ist sehr
            pauschal" (Fachabstimmung 31.08.2026). Pflicht heißt hier: markiert
            und in der Zusammenfassung als fehlend gemeldet, „Weiter" bleibt
            frei (Entscheidung 21.10.2026). Nicht bei reinem Gewerbe. */}
        {wohnflaecheGefragt(a) && (
          <QuestionBlock
            question={{ question: "Wohnfläche des Gebäudes (m²) *" }}
            answers={a}
            onChange={onChange}
          >
            <GrossZahlEingabe
              value={a.wohnflaeche}
              onChange={(v) => onChange("wohnflaeche", v)}
              ariaLabel="Wohnfläche (m²)"
              name="wohnflaeche"
              einheit="m²"
              dezimal
            />
            {!a.wohnflaeche && (
              <p style={{ fontFamily: BRAND.fontPrimary, fontSize: "0.82rem", color: "#a4262c", margin: "6px 0 0" }}>
                Pflichtangabe — bitte die Wohnfläche eintragen.
              </p>
            )}
          </QuestionBlock>
        )}

        <QuestionBlock
          question={{
            question: "Nutzen Sie das Objekt selbst (auch teilweise)?",
            field: {
              key: "eigennutzung",
              type: "select",
              options: EIGENNUTZUNG_OPTIONS.map((o) => ({
                value: o,
                label:
                  o === "ja"
                    ? "Ja, vollständig"
                    : o === "teilweise"
                      ? "Teilweise"
                      : "Nein – vermietet",
              })),
            },
            showFollowUpWhen: { key: "eigennutzung", equals: "teilweise" },
            followUpFields: [
              { key: "eigennutzungWas", label: "Was nutzen Sie selbst?", type: "text" },
            ],
          }}
          answers={a}
          // Die Antwort beantwortet zugleich die Vermietung — im Schritt
          // „Beteiligte" wird sie deshalb nicht noch einmal gefragt
          // (Fachabstimmung 31.08.2026). `vermietung` wird hier mitgeschrieben.
          onChange={(k, v) =>
            k === "eigennutzung"
              ? setGebaeudeAnswers(g.id, {
                  eigennutzung: v,
                  vermietung: v === "ja" ? "nein" : v ? "ja" : "",
                })
              : onChange(k, v)
          }
        />

        {/* Das ausdrückliche Ja/Nein-Gate bildet den Spec-Wortlaut ab
            („Auswahl wenn ja") und hält ein bewusstes „nein" fest: der
            Speicherpfad verwirft leere Listen, ohne Gate wäre „keine
            Stellplätze" von „nie ausgefüllt" nicht zu unterscheiden. */}
        <QuestionBlock
          question={{
            question: "Sind Tiefgaragen, Garagen oder Carports vorhanden?",
            field: { key: "stellplatzVorhanden", type: "yesno" },
          }}
          answers={{ ...a, stellplatzVorhanden: stellplatzGate }}
          onChange={onChange}
        >
          {stellplatzGate === "ja" && (
            <StellplatzBlock
              geschosse={geschossOptionen(a.geschosseUeber, a.geschosseUnter)}
              arten={stellplatzArten}
              details={stellplatzDetails}
              legacyLage={
                typeof a.stellplatzLage === "string" ? a.stellplatzLage : undefined
              }
              legacyAnzahl={
                typeof a.stellplatzAnzahl === "string"
                  ? a.stellplatzAnzahl
                  : a.stellplatzAnzahl != null
                    ? String(a.stellplatzAnzahl)
                    : undefined
              }
              onChange={(next) =>
                setGebaeudeAnswers(g.id, {
                  stellplatzArten: next.arten,
                  stellplatzDetails: next.details,
                })
              }
            />
          )}
        </QuestionBlock>

        {/*
          Leerstand war bis 19.08.2026 ein eigener Schritt und steht jetzt am
          Ende der Bauart (Auftraggeber). Der Block ist unverändert übernommen —
          `leerstand` und die sieben `leerstand*`-Folgefelder behalten ihre
          Schlüssel, es entfällt nur die Seitengrenze davor.
        */}
        <QuestionBlock
          question={leerstandFrage(Boolean(a.leerstandPlan))}
          answers={a}
          onChange={onChange}
        />
      </div>
    );
  }

  function renderDach(g: Gebaeude): JSX.Element {
    return (
      <div className={styles.questions}>
        <DachPicker
          value={(g.answers.dachTyp as string) ?? ""}
          ausgebaut={(g.answers.dachAusgebaut as string) ?? ""}
          onChange={(v) => setGebaeudeAnswer(g.id, "dachTyp", v)}
          onAusgebautChange={(v) => setGebaeudeAnswer(g.id, "dachAusgebaut", v)}
        />
      </div>
    );
  }

  /**
   * Schritt „Anzahl der Einheiten" — seit 19.08.2026 EIN Schritt aus drei
   * ehemaligen (Auftraggeber): die Einheiten und die Kunst am Bau.
   *
   * Beim Einfamilien-/Zweifamilienhaus steht die Anzahl fest, gefragt wird nur
   * nach der Einliegerwohnung; je Einheit gibt es dort keine Detail-Felder,
   * die Liste bleibt also aus. Sonst ist die Liste die einzige Eingabe (siehe
   * unten), eine getrennte Anzahl gibt es seit 10/2026 nicht mehr.
   */
  function renderEinheiten(g: Gebaeude): JSX.Element {
    const kategorie = (g.answers.kategorie as string) ?? "";
    const typ = (g.answers.typ as string) ?? "";
    const profile = profileForType(kategorie, typ);
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);

    // Die Anzahl wird nicht mehr getrennt abgefragt: „Wenn du oben vier
    // eingibst, brauchst du das nicht mehr" (Fachabstimmung 31.08.2026). Die
    // Liste ist die einzige Quelle, `anzahlEinheiten` wird bei jeder Änderung
    // aus ihrer Länge mitgeschrieben (Zusammenfassung, Immobilienmappe und
    // Vertragsmaske lesen den Schlüssel weiter).
    //
    // Bestandsdaten mit einer Anzahl, aber ohne Liste, bekommen beim Öffnen so
    // viele leere Karten angezeigt. Erst eine Eingabe schreibt die Liste.
    // Früher füllte die Anzeige bei JEDER Änderung bis zur Anzahl auf — wer
    // eine Einheit entfernte, sah sofort wieder eine leere.
    const existing = (g.answers.einheiten as Einheit[] | undefined) ?? [];
    const zeigeSpezifizierung = profile.fields.length > 0;
    const startAnzahl = Math.max(Number(g.answers.anzahlEinheiten ?? 0) || 0, 1);
    const display: Einheit[] =
      existing.length > 0
        ? existing
        : Array.from({ length: Math.min(startAnzahl, 50) }, () => ({}) as Einheit);
    const setEinheiten = (next: Einheit[]) =>
      setGebaeudeAnswers(g.id, {
        einheiten: next,
        anzahlEinheiten: next.length > 0 ? String(next.length) : "",
      });

    return (
      <div className={styles.questions}>
        {profile.countLocked && (
          <QuestionBlock
            question={{
              question: `Bei ${typ} sind ${profile.countLocked} Einheit(en) gesetzt.`,
              hint: "Bitte geben Sie an, ob eine Einliegerwohnung vorhanden ist.",
              field: { key: "einliegerwohnung", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
        )}

        {zeigeSpezifizierung && (
          <div>
            <h3 style={headingStyle()}>
              Einheiten des Gebäudes
            </h3>
            <EinheitenList
              kategorie={kategorie}
              typ={typ}
              einheiten={display}
              onChange={setEinheiten}
              countLockedFromTyp={profile.countLocked}
              // `scope` ist die Gebäude-UUID und muss es bleiben: die
              // Datei-Slots der Einheiten (`einheitSlotKey(idx)`) hängen daran.
              // Ein anderer Wert verlöre die Zuordnung vorgemerkter
              // Mietverträge.
              scope={g.id}
              onUnitRemoved={(idx) => uploadBuffer.remapEinheitenSlots(g.id, idx)}
              ansprache={ansprache}
            />
            {/* Die Gesamtfläche wird im Schritt „Bauart" erfragt — der steht in
                der Schrittkette aber VOR den Einheiten. Deshalb steht der
                Abgleich zusätzlich hier, wo die Flächen entstehen. */}
            <GesamtflaecheBlock
              variante="abgleich"
              einheiten={g.answers.einheiten}
              value={g.answers.gesamtflaeche}
              onChange={(v) => setGebaeudeAnswer(g.id, "gesamtflaeche", v)}
            />
          </div>
        )}

        {/* Seit 10/2026 mehrere Objekte mit Vorgaben statt eines Beschreibungs-
            und eines Wertfelds; die früheren Felder werden zum ersten Eintrag. */}
        <QuestionBlock question={KUNST_FRAGE} answers={a} onChange={onChange}>
          {a.kunst === "ja" && (
            <KunstListe
              entries={kunstAusAntworten(a)}
              onChange={(next: KunstObjekt[]) =>
                setGebaeudeAnswers(g.id, {
                  kunstObjekte: next,
                  ...Object.fromEntries(KUNST_ALT_SCHLUESSEL.map((k) => [k, ""])),
                })
              }
            />
          )}
        </QuestionBlock>
      </div>
    );
  }

  function renderBranchen(g: Gebaeude): JSX.Element {
    const branchen = (g.answers.branchen as BranchenSelections) ?? {};
    return (
      <div className={styles.questions}>
        <BranchenChecklist
          value={branchen}
          onChange={(next) => setGebaeudeAnswer(g.id, "branchen", next)}
        />
      </div>
    );
  }

  function renderAussenanlagen(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    const q: QuestionDef = {
      question:
        "Sind auf dem Grundstück wertrelevante Außenanlagen oder sonstige Grundstücksbestandteile vorhanden?",
      field: { key: "aussenanlagen", type: "yesno" },
      showFollowUpWhen: { key: "aussenanlagen", equals: "ja" },
      followUpFields: [
        {
          key: "aussenanlagenArt",
          label: "Art der Außenanlage",
          type: "text",
          placeholder: "z. B. Pflaster, Grünanlagen, Zaun, Spielplatz",
        },
        { key: "aussenanlagenWert", label: "Wert (€)", type: "number" },
      ],
    };
    return (
      <div className={styles.questions}>
        <QuestionBlock question={q} answers={a} onChange={onChange} />
      </div>
    );
  }

  /**
   * „Bauausführung" (xlsx Z. 7, Punkt 12 ff.) — Bewertung, Denkmalschutz und
   * die beiden Ausstattungskataloge.
   *
   * Der Schritt war als einziger der Gebäudestrecke reine Schrift. Bewertung
   * und Kataloge sind jetzt Bildauswahlen (`AusstattungsstufePicker`,
   * `AusstattungsKatalog`); die Antwort-Schlüssel und ihre Werte sind dabei
   * unverändert geblieben — `bauausfuehrungBewertung` speichert weiter
   * „gehoben"/„normal"/„minder", die Kataloge weiter
   * `{ [katalogschluessel]: { selected: "true", … } }`.
   *
   * **Ohne Bild bleibt der Denkmalschutz.** Er ist ein Rechtsstatus, kein
   * Baumerkmal: ein geschütztes und ein ungeschütztes Gebäude sehen gleich aus,
   * jedes Symbol dafür wäre erfunden. Ebenso die Zusatzfelder der Kataloge
   * (Wert, Einbaudatum, Anteil) und der Freitext.
   */
  function renderBauausfuehrung(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    const sonderausstattungen =
      (a.sonderausstattungen as Record<string, Record<string, string>>) ?? {};
    const setSonder = (key: string, sub: string, v: string) => {
      onChange("sonderausstattungen", {
        ...sonderausstattungen,
        [key]: { ...(sonderausstattungen[key] ?? {}), [sub]: v },
      });
    };

    const wellness = (a.wellness as Record<string, Record<string, string>>) ?? {};
    const setWellness = (key: string, sub: string, v: string) => {
      onChange("wellness", {
        ...wellness,
        [key]: { ...(wellness[key] ?? {}), [sub]: v },
      });
    };

    const bewertung = (a.bauausfuehrungBewertung as string) ?? "";
    // Begründungspflicht unverändert: alles außer „normal", aber erst nach
    // einer Auswahl. Das entspricht genau dem abgelösten
    // `showFollowUpWhen: { key: …, notEquals: "normal" }` — `matchesCondition`
    // verlangt dort ebenfalls eine gefüllte Antwort.
    const begruendungNoetig = bewertung !== "" && bewertung !== "normal";

    const questions: QuestionDef[] = [
      {
        question: "Steht das Gebäude ganz oder teilweise unter Denkmalschutz?",
        field: { key: "denkmalschutz", type: "yesno" },
        showFollowUpWhen: { key: "denkmalschutz", equals: "ja" },
        followUpFields: [
          { key: "denkmalAnteil", label: "Anteil (%)", type: "number" },
          {
            key: "denkmalBeschreibung",
            label: "Betroffene Gebäudeteile",
            type: "textarea",
          },
        ],
      },
    ];

    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/* Die Bildauswahl braucht mehr als ein Feld je Zeile und hängt deshalb
            in einer feldlosen `QuestionDef` im children-Slot — dasselbe Muster
            wie Bauartklasse und Stellung im Schritt „Bauart". Die Begründung
            steht als eigener Hinweiskasten DARUNTER statt in `followUpFields`:
            `QuestionBlock` zeichnet den Folgefragen-Kasten vor den children, er
            stünde sonst über der Auswahl, auf die er sich bezieht. */}
        <QuestionBlock
          question={{ question: "Wie würden Sie Bauausführung und Ausstattung des Gebäudes bewerten?" }}
          answers={a}
          onChange={onChange}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <AusstattungsstufePicker
              value={bewertung}
              onChange={(v) => onChange("bauausfuehrungBewertung", v)}
            />
            {begruendungNoetig && (
              <div style={{ ...revealStyle(), gridTemplateColumns: "1fr" }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                  <span style={smallLabelStyle()}>Bitte erläutern (wodurch / warum?)</span>
                  <textarea
                    style={{ ...inputStyle(), minHeight: 80 }}
                    aria-label="Bitte erläutern (wodurch / warum?)"
                    value={(a.bauausfuehrungBegruendung as string) ?? ""}
                    onChange={(e) => onChange("bauausfuehrungBegruendung", e.target.value)}
                  />
                </div>
              </div>
            )}
          </div>
        </QuestionBlock>

        {questions.map((q, i) => (
          <QuestionBlock key={i} question={q} answers={a} onChange={onChange} />
        ))}
        <div>
          <h3 style={headingStyle()}>Besondere Ausstattungen oder technische Sonderbestandteile</h3>
          <AusstattungsKatalog
            gruppe="Besondere Ausstattungen oder technische Sonderbestandteile"
            feldPraefix="sonderausstattung"
            eintraege={SONDERAUSSTATTUNGEN.filter((e) =>
              ausstattungSichtbar(e, a, sonderausstattungen as Record<string, unknown>),
            )}
            werte={sonderausstattungen}
            onSet={setSonder}
          />
          <div style={{ marginTop: 12 }}>
            <span style={smallLabelStyle()}>Sonstige Sonderausstattung</span>
            <textarea
              style={{ ...inputStyle(), minHeight: 80 }}
              aria-label="Sonstige Sonderausstattung"
              value={(a.sonderausstattungenSonstige as string) ?? ""}
              onChange={(e) => onChange("sonderausstattungenSonstige", e.target.value)}
            />
          </div>
        </div>
        <div>
          <h3 style={headingStyle()}>Wellness-Einrichtungen</h3>
          <AusstattungsKatalog
            gruppe="Wellness-Einrichtungen"
            feldPraefix="wellness"
            eintraege={WELLNESS_EINRICHTUNGEN}
            werte={wellness}
            onSet={setWellness}
          />
        </div>
      </div>
    );
  }

  function renderSicherheit(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
        {/*
          Die fünf Hauptanlagen werden ausdrücklich mit Ja ODER Nein
          beantwortet — ihre Schlüssel `sicherheit_bma` … `sicherheit_blitzschutz`
          liegen so in Bestandsdaten und dürfen sich nicht ändern.
        */}
        {anlagenDerGruppe("haupt")
          .filter((eintrag) => anlageSichtbar(eintrag.key, a))
          .map((eintrag) => {
          const key = anlagenAnswerKey(eintrag.key);
          const entry = (a[key] as AnlagenEntry | undefined) ?? {};
          return (
            <AnlagenBlock
              key={eintrag.key}
              anlage={eintrag.key}
              title={eintrag.label}
              value={entry}
              onChange={(next) => onChange(key, next)}
              scope={g.id}
              slotKey={anlagenSlotKey(eintrag.key)}
              ansprache={ansprache}
              variant="jaNein"
              showErrichter={zeigtErrichter(eintrag)}
              extraFields={eintrag.extraFields}
            />
          );
        })}
        {/*
          EINE Frage mit zwei Abschnitten — „Feuer, Brand" und „Elektrische
          Anlagen …" sind Zwischenüberschriften, keine eigenen Fragen.
        */}
        <h3 style={{ ...headingStyle(), marginTop: 8, marginBottom: 0 }}>
          {ANLAGEN_AUSWAHL_FRAGE}
        </h3>
        {ANLAGEN_GRUPPEN.filter((gruppe) => gruppe.key !== "haupt").map((gruppe) => (
          <AnlagenChecklist
            key={gruppe.key}
            gruppe={gruppe.key}
            titel={gruppe.titel}
            answers={a}
            onChange={onChange}
            scope={g.id}
            ansprache={ansprache}
          />
        ))}
        <div style={cardStyle()}>
          <QuestionBlock
            question={{
              /*
                „Einfach ja/nein ohne die Frage" (Auftraggeber, 19.08.2026):
                statt des Fragesatzes „Liegt ein Brandschutzkonzept vor?" nur
                noch die schlichte Beschriftung — und ohne die Folgefelder
                „Von wem erstellt?" / „Datum (von wann)?".
              */
              question: "Brandschutzkonzept",
              field: { key: "brandschutzDokumentVorhanden", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.brandschutzDokumentVorhanden === "ja" && (
            <UploadField
              scope={g.id}
              slotKey="brandschutzkonzept"
              label="Brandschutzkonzept"
              ansprache={ansprache}
            />
          )}
        </div>
        {notstromSichtbar(a) && (
          <QuestionBlock
            question={{
              question: "Notstromversorgung vorhanden?",
              field: { key: "notstrom", type: "yesno" },
              showFollowUpWhen: { key: "notstrom", equals: "ja" },
              followUpFields: [
                { key: "notstromArt", label: "Art der Notstromversorgung", type: "text" },
                { key: "notstromWert", label: "Wert (Neuwert, brutto)", type: "number" },
              ],
            }}
            answers={a}
            onChange={onChange}
          />
        )}
      </div>
    );
  }

  /**
   * Die drei Präventionsblöcke. Eigener Schritt und ausdrücklich KEINE
   * Katalog-Einträge: es sind keine Anlagen mit Errichter und Wartungsintervall,
   * sondern Systeme — die Folgefragen sind andere.
   */
  function renderPraevention(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    return (
      <div className={styles.questions}>
        {PRAEVENTIONS_BLOECKE.map((block) => (
          <QuestionBlock
            key={block.key}
            question={praeventionsFrage(block)}
            answers={a}
            onChange={onChange}
          />
        ))}
      </div>
    );
  }

  function renderEnergie(g: Gebaeude): JSX.Element {
    const heizungen = (g.answers.heizungen as HeizungSelections) ?? {};
    return (
      <div className={styles.questions}>
        <HeizungsChecklist
          value={heizungen}
          onChange={(next) => setGebaeudeAnswer(g.id, "heizungen", next)}
        />
      </div>
    );
  }

  function renderUmwelt(g: Gebaeude): JSX.Element {
    const umwelt = (g.answers.umwelt as UmweltSelections) ?? {};
    return (
      <div className={styles.questions}>
        <UmweltChecklist
          value={umwelt}
          onChange={(next) => setGebaeudeAnswer(g.id, "umwelt", next)}
          oilTanksForced={isOilHeating(g.answers)}
        />
      </div>
    );
  }

  function renderVersicherungsbedarf(g: Gebaeude): JSX.Element {
    return (
      <div className={styles.questions}>
        <VersicherungsbedarfBlock
          answers={g.answers}
          onChange={(next) => setGebaeudeAnswer(g.id, "versicherungsbedarf", next)}
        />
      </div>
    );
  }

  function renderAnbauten(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    // Mehrere Anbauten mit derselben Detailerfassung wie das Hauptgebäude.
    // Die früheren Einzelfelder (`anbautenGebaeudeart` …) erscheinen als erster
    // Eintrag und werden beim ersten Bearbeiten geleert.
    const anbauten = anbautenAusAntworten(a);
    return (
      <div className={styles.questions}>
        <QuestionBlock
          question={{
            question: "Gibt es Anbauten oder weitere Gebäude auf dem Grundstück?",
            hint: "z. B. Garage, Wintergarten, Werkstatt, Scheune oder ein zweites Haus",
            field: { key: "anbautenJa", type: "yesno" },
          }}
          answers={a}
          onChange={onChange}
        >
          {a.anbautenJa === "ja" && (
            <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <AnbautenListe
                entries={anbauten}
                onChange={(next: Anbau[]) =>
                  setGebaeudeAnswers(g.id, {
                    anbauten: next,
                    ...Object.fromEntries(ANBAU_ALT_SCHLUESSEL.map((k) => [k, ""])),
                  })
                }
              />
              {/* Über den Kundenlink gehört der Link zu genau EINEM Risiko —
                  ein zweites Gebäude ließe sich dort nicht speichern. */}
              {!kundenlink && (
              <p style={{ fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", color: BRAND.muted, margin: 0 }}>
                Ein weiteres großes Gebäude mit eigener Nutzung (z. B. ein zweites
                Mehrfamilienhaus) lässt sich auch vollständig mit allen Schritten erfassen.{" "}
                <button
                  type="button"
                  onClick={() => {
                    setGlobalField("hasMultiple", "ja");
                    addGebaeude();
                  }}
                  style={{
                    background: "none",
                    border: "none",
                    padding: 0,
                    color: BRAND.purple,
                    fontWeight: 700,
                    cursor: "pointer",
                    fontFamily: BRAND.fontPrimary,
                    fontSize: "0.85rem",
                  }}
                >
                  + Als eigenes Gebäude erfassen
                </button>
              </p>
              )}
            </div>
          )}
        </QuestionBlock>
      </div>
    );
  }


  /**
   * „Eigentum & Rechte" (xlsx Z. 26-38, 57-63) — wem gehört das Objekt und
   * welche Rechte liegen darauf.
   *
   * Regelfall ist Einzeleigentum (Entscheidung E2): erst **ein** Eigentümer
   * über die Kontaktauswahl, danach die Folgefrage nach weiteren
   * Miteigentümern. Erst bei „ja" erscheint die Liste — nicht umgekehrt.
   *
   * Vor jeder Liste steht ein ausdrückliches Ja/Nein-Gate. Grund: `PUT
   * /risk-objects/:id` ist ein Replace-Set und der Speicherfilter verwirft
   * leere Werte — ohne Gate verschwände ein bewusstes „nein" ersatzlos,
   * statt als Antwort erhalten zu bleiben.
   */
  function renderEigentum(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    const kontakt = (
      kontaktKey: string,
      nameKey: string,
      label: string,
      dialogTitle: string,
    ) => (
      <KontaktFeld
        kontaktKey={kontaktKey}
        nameKey={nameKey}
        label={label}
        dialogTitle={dialogTitle}
        lookup={contactLookup}
        ansprache={ansprache}
        // Der Hinweis zur Erfassung steht einmal oben im Schritt — an jedem
        // der rund zehn Felder wäre er nur Rauschen.
        hideHint
        answers={g.answers}
        onChange={onChange}
      />
    );

    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {!contactLookup && (
          <p style={{ fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", color: BRAND.muted, marginTop: 0 }}>
            {immoText("kontaktOhneLookup", ansprache)}
          </p>
        )}
        <div style={cardStyle()}>
          <h3 style={headingStyle()}>Eigentümer</h3>
          <KontaktFeld
            kontaktKey="eigentuemerKontaktRef"
            nameKey="eigentuemerName"
            label="Eigentümer"
            dialogTitle="Eigentümer auswählen"
            lookup={contactLookup}
            ansprache={ansprache}
            hideHint
            answers={g.answers}
            onRefChange={(ref) => {
              const vorher = asKontaktRef(a.eigentuemerKontaktRef)?.contactId ?? null;
              setGebaeudeAnswers(g.id, { eigentuemerKontaktRef: ref, eigentuemerName: ref.label });
              // Anschrift aus dem gewählten Kontakt übernehmen — danach
              // abweichend änderbar. Bisher geschah das nur beim Token-Prefill.
              if (contactLookup && ref.contactId && ref.contactId !== vorher) {
                ladeKontaktStammdaten(ref.contactId)
                  .then(({ anschrift }) => {
                    if (!anschrift) return;
                    setGebaeudeAnswers(g.id, {
                      eigentuemerStrasse: anschrift.street,
                      eigentuemerPlz: anschrift.postalCode,
                      eigentuemerOrt: anschrift.city,
                      eigentuemerLand: anschrift.country || "Deutschland",
                    });
                  })
                  .catch(() => {});
              }
            }}
          />
          <div style={{ marginTop: 12 }}>
            <AdressFelder
              idPrefix={`eigentuemer-${g.id}`}
              keys={{
                street: "eigentuemerStrasse",
                postalCode: "eigentuemerPlz",
                city: "eigentuemerOrt",
                country: "eigentuemerLand",
              }}
              values={{
                street: (a.eigentuemerStrasse as string) ?? "",
                postalCode: (a.eigentuemerPlz as string) ?? "",
                city: (a.eigentuemerOrt as string) ?? "",
                country: (a.eigentuemerLand as string) ?? "",
              }}
              onChange={onChange}
            />
          </div>
          <div style={{ marginTop: 12 }}>
            <QuestionBlock
              question={{
                question: "Eigentumsanteil",
                field: {
                  key: "eigentuemerAnteilProzent",
                  label: "Anteil in %",
                  type: "number",
                  hint: "Bei Alleineigentum 100.",
                },
              }}
              answers={a}
              onChange={onChange}
            />
          </div>
          {/* „Vertreten durch" direkt am Eigentümer (Fachabstimmung 31.08.2026:
              „Muss ich ja hier dann auch direkt machen können"). Die Schlüssel
              (`hvBeauftragt`, `hvKontaktRef` …) sind dieselben wie vorher im
              Schritt „Beteiligte". */}
          <div style={{ marginTop: 12 }}>
            <div>
              <QuestionBlock
                question={{
                  question: "Wird der Eigentümer durch eine Verwaltung vertreten?",
                  hint: "z. B. „WEG Lagerhausstraße, vertreten durch Hausverwaltung X“",
                  field: { key: "hvBeauftragt", type: "yesno" },
                }}
                answers={a}
                onChange={onChange}
              />
              {a.hvBeauftragt === "ja" && (
                <div style={cardStyle()}>
                  <QuestionBlock
                    question={{
                      question: "Art der Verwaltung",
                      field: {
                        key: "hvArt",
                        type: "select",
                        options: VERWALTUNGS_ARTEN.map((v) => ({ value: v, label: v })),
                      },
                    }}
                    answers={a}
                    onChange={onChange}
                  />
                  <div style={{ marginTop: 12 }}>
                    {kontakt("hvKontaktRef", "hvName", "Verwaltung", "Verwaltung auswählen")}
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <QuestionBlock
                      question={{
                        question: "Weitere Angaben zur Verwaltung",
                        fields: [
                          // `hvKontakt` hält seit jeher die **Kontaktdaten** (Telefon,
                          // E-Mail, Anschrift) als Freitext — nicht den Namen. Es
                          // bleibt deshalb unangetastet; der Name liegt in `hvName`.
                          { key: "hvKontakt", label: "Kontaktdaten der Verwaltung", type: "text" },
                          {
                            key: "hvObjektId",
                            label: "Objekt-ID der Verwaltung",
                            type: "text",
                            hint: "Interne Bezeichnung bei der Verwaltung, z. B. „Verwaltungseinheit 22“ — optional.",
                          },
                        ],
                        layout: "two",
                      }}
                      answers={a}
                      onChange={onChange}
                    />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <AnsprechpartnerAuswahl
                      contactId={asKontaktRef(a.hvKontaktRef)?.contactId ?? null}
                      lookup={contactLookup}
                      werte={a}
                      onChange={(patch) => setGebaeudeAnswers(g.id, patch)}
                    />
                  </div>
                  <div style={{ marginTop: 12 }}>
                    <UploadField
                      scope={g.id}
                      slotKey="hv-vertrag"
                      label="Verwaltervertrag"
                      ansprache={ansprache}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <div>
          <QuestionBlock
            question={{
              question: "Gibt es weitere Miteigentümer?",
              field: { key: "miteigentuemerJa", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.miteigentuemerJa === "ja" && (
            <div style={{ marginTop: 12 }}>
              <BeteiligtenListe
                listenKey="miteigentuemer"
                entries={asBeteiligtenListe(a.miteigentuemer)}
                onChange={(next) => onChange("miteigentuemer", next)}
                singular="Miteigentümer"
                dialogTitle="Miteigentümer auswählen"
                felder={["anteilProzent", "bemerkung"]}
                lookup={contactLookup}
                ansprache={ansprache}
              />
            </div>
          )}
        </div>

        <QuestionBlock question={{ question: "Hauskonto" }} answers={a} onChange={onChange}>
          <HauskontoAuswahl
            contactId={asKontaktRef(a.eigentuemerKontaktRef)?.contactId ?? null}
            lookup={contactLookup}
            werte={a}
            onChange={(patch) => setGebaeudeAnswers(g.id, patch)}
          />
        </QuestionBlock>

        <QuestionBlock
          question={{
            question: "Objekt-ID des Eigentümers",
            field: {
              key: "objektIdEigentuemer",
              type: "text",
              hint: "Interne Bezeichnung beim Eigentümer, z. B. „Verwaltungseinheit 22“ — optional.",
            },
          }}
          answers={a}
          onChange={onChange}
        />

        <div style={cardStyle()}>
          <h3 style={headingStyle()}>Rechnungsempfänger (Versicherungsnehmer)</h3>
          {kontakt(
            "rechnungsempfaengerKontaktRef",
            "rechnungsempfaenger",
            "Rechnungsempfänger",
            "Rechnungsempfänger auswählen",
          )}
        </div>

        <div>
          <QuestionBlock
            question={{
              question: "Sind Nießbrauchberechtigte vorhanden?",
              field: { key: "niessbrauch", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.niessbrauch === "ja" && (
            <div style={cardStyle()}>
              {kontakt(
                "niessbrauchKontaktRef",
                "niessbrauchWer",
                "Nießbrauchberechtigter",
                "Nießbrauchberechtigten auswählen",
              )}
              <div style={{ marginTop: 12 }}>
                <QuestionBlock
                  question={{
                    question: "Umfang des Nießbrauchs",
                    fields: [
                      { key: "niessbrauchProzent", label: "Wieviel %?", type: "number" },
                      {
                        key: "niessbrauchEinheit",
                        label: "Betroffene Einheit / Fläche",
                        type: "text",
                      },
                    ],
                    layout: "two",
                  }}
                  answers={a}
                  onChange={onChange}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                {kontakt(
                  "niessbrauchBelastetKontaktRef",
                  "niessbrauchBelastetName",
                  "Wer ist mit dem Nießbrauch belastet?",
                  "Belasteten auswählen",
                )}
              </div>
            </div>
          )}
        </div>

        <div>
          <QuestionBlock
            question={{
              question: "Besteht ein Erbbaurecht am Objekt?",
              field: { key: "erbbaurecht", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.erbbaurecht === "ja" && (
            <div style={cardStyle()}>
              {kontakt(
                "erbbauberechtigterKontaktRef",
                "erbbauberechtigterName",
                "Erbbauberechtigter",
                "Erbbauberechtigten auswählen",
              )}
              <div style={{ marginTop: 12 }}>
                <QuestionBlock
                  question={{
                    question: "Anteil bzw. Umfang des Erbbaurechts",
                    field: { key: "erbbaurechtUmfang", type: "text" },
                  }}
                  answers={a}
                  onChange={onChange}
                />
              </div>
              <div style={{ marginTop: 12 }}>
                {kontakt(
                  "erbbaugeberKontaktRef",
                  "erbbaugeberName",
                  "Erbbaurechtsgeber",
                  "Erbbaurechtsgeber auswählen",
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * „Finanzierung / Sicherungsrechte" (xlsx Z. 62-63) — seit E14 ein eigener
   * Schritt. Vorher standen dieselben Fragen unten im Eigentümer-Bereich; die
   * **Answer-Keys sind unverändert** (`darlehenJa`, `darlehenInstitut`,
   * `darlehenNummer`, `darlehenAblauf`, `darlehenSicherung`), es wandert nur
   * die Darstellung. Bestandsdaten verwaisen dadurch nicht.
   */
  function renderFinanzierung(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    const kontakt = (
      kontaktKey: string,
      nameKey: string,
      label: string,
      dialogTitle: string,
    ) => (
      <KontaktFeld
        kontaktKey={kontaktKey}
        nameKey={nameKey}
        label={label}
        dialogTitle={dialogTitle}
        lookup={contactLookup}
        ansprache={ansprache}
        hideHint
        answers={g.answers}
        onChange={onChange}
      />
    );
    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <QuestionBlock
          question={{
            question: "Bestehen Darlehen / Sicherungsscheine / Zweckbestimmungen?",
            field: { key: "darlehenJa", type: "yesno" },
          }}
          answers={a}
          onChange={onChange}
        />
        {a.darlehenJa === "ja" && (
          <div style={cardStyle()}>
            {kontakt(
              "darlehenInstitutKontaktRef",
              "darlehenInstitut",
              "Finanzierendes Institut",
              "Institut auswählen",
            )}
            <div style={{ marginTop: 12 }}>
              <QuestionBlock
                question={{
                  question: "Angaben zum Darlehen",
                  fields: [
                    { key: "darlehenNummer", label: "Darlehensnummer", type: "text" },
                    { key: "darlehenAblauf", label: "Ablauf", type: "date" },
                    {
                      key: "darlehenSicherung",
                      label: "Sicherungsschein / Zweckbestimmung",
                      type: "text",
                    },
                  ],
                  layout: "two",
                }}
                answers={a}
                onChange={onChange}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  /**
   * „Gutachten" (xlsx Z. 57-61) — seit E14 ein eigener Schritt, Answer-Keys
   * unverändert (`gutachten`, `gutachtenErsteller`, `gutachtenDatum`,
   * `gutachtenStichtag`, `gutachtenGrund`).
   */
  function renderGutachten(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    const kontakt = (
      kontaktKey: string,
      nameKey: string,
      label: string,
      dialogTitle: string,
    ) => (
      <KontaktFeld
        kontaktKey={kontaktKey}
        nameKey={nameKey}
        label={label}
        dialogTitle={dialogTitle}
        lookup={contactLookup}
        ansprache={ansprache}
        hideHint
        answers={g.answers}
        onChange={onChange}
      />
    );
    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        <QuestionBlock
          question={{
            question: "Gibt es ein Gutachten zum Objekt?",
            field: { key: "gutachten", type: "yesno" },
          }}
          answers={a}
          onChange={onChange}
        />
        {a.gutachten === "ja" && (
          <div style={cardStyle()}>
            {kontakt(
              "gutachtenErstellerKontaktRef",
              "gutachtenErsteller",
              "Ersteller des Gutachtens",
              "Gutachter auswählen",
            )}
            <div style={{ marginTop: 12 }}>
              <QuestionBlock
                question={{
                  question: "Angaben zum Gutachten",
                  fields: [
                    { key: "gutachtenDatum", label: "Datum der Erstellung", type: "date" },
                    { key: "gutachtenStichtag", label: "Stichtag der Bewertung", type: "date" },
                    { key: "gutachtenGrund", label: "Grund der Bewertung", type: "text" },
                  ],
                  layout: "two",
                }}
                answers={a}
                onChange={onChange}
              />
            </div>
            <div style={{ marginTop: 12 }}>
              <UploadField
                scope={g.id}
                slotKey="gutachten"
                label="Gutachten"
                ansprache={ansprache}
              />
            </div>
          </div>
        )}
      </div>
    );
  }

  /**
   * „Beteiligte am Objekt" (xlsx Z. 33, 39-56) — alle weiteren Personen und
   * Unternehmen: Verwaltung, Mieter, Pächter, Betreiber, sonstige Nutzer,
   * Facility Management und weitere Verbundene.
   *
   * Zwangs- und Sondereigentumsverwaltung sind **keine** eigenen Rollen,
   * sondern Untertypen der einen Rolle „Verwaltung" (Entscheidung E3). Der
   * tatsächliche Eigentümer bleibt in jedem Fall im Schritt davor erfasst.
   */
  function renderBeteiligte(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const onChange = (k: string, v: unknown) => setGebaeudeAnswer(g.id, k, v);
    // Bestand kennt die Eigennutzung oft schon ohne `vermietung` — dann daraus
    // ableiten, sonst bliebe die Mieterliste verborgen.
    const vermietet =
      (a.vermietung as string | undefined) ||
      (a.eigennutzung ? (a.eigennutzung === "ja" ? "nein" : "ja") : "");
    const kontakt = (
      kontaktKey: string,
      nameKey: string,
      label: string,
      dialogTitle: string,
    ) => (
      <KontaktFeld
        kontaktKey={kontaktKey}
        nameKey={nameKey}
        label={label}
        dialogTitle={dialogTitle}
        lookup={contactLookup}
        ansprache={ansprache}
        // Der Hinweis zur Erfassung steht einmal oben im Schritt — an jedem
        // der rund zehn Felder wäre er nur Rauschen.
        hideHint
        answers={g.answers}
        onChange={onChange}
      />
    );

    return (
      <div className={styles.questions} style={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {!contactLookup && (
          <p style={{ fontFamily: BRAND.fontPrimary, fontSize: "0.85rem", color: BRAND.muted, marginTop: 0 }}>
            {immoText("kontaktOhneLookup", ansprache)}
          </p>
        )}
        <div>
          {/* Steht die Eigennutzung schon fest („Bauart"), ist die Vermietung
              damit beantwortet — dann nur noch die Mieter. */}
          {a.eigennutzung ? (
            vermietet === "ja" && (
              <h3 style={headingStyle()}>
                Mieter
                <span style={{ fontWeight: 400, fontSize: "0.85rem", color: BRAND.muted }}>
                  {" "}
                  — laut Angabe „{a.eigennutzung === "teilweise" ? "teilweise selbst genutzt" : "vermietet"}“
                </span>
              </h3>
            )
          ) : (
            <QuestionBlock
              question={{
                question: "Ist das Objekt ganz oder teilweise vermietet?",
                field: { key: "vermietung", type: "yesno" },
              }}
              answers={a}
              onChange={onChange}
            />
          )}
          {vermietet === "ja" && (
            <div style={{ marginTop: 12 }}>
              <BeteiligtenListe
                listenKey="mieter"
                entries={asBeteiligtenListe(a.mieter)}
                onChange={(next) => onChange("mieter", next)}
                singular="Mieter"
                dialogTitle="Mieter auswählen"
                felder={["einheit", "von", "bis"]}
                labels={{ von: "Mietbeginn", bis: "Mietende (leer = unbefristet)" }}
                lookup={contactLookup}
                ansprache={ansprache}
              />
              <div style={{ marginTop: 12 }}>
                <UploadField
                  scope={g.id}
                  slotKey="mietvertraege"
                  label="Mietverträge"
                  ansprache={ansprache}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <QuestionBlock
            question={{
              question: "Ist das Objekt ganz oder teilweise verpachtet?",
              field: { key: "verpachtung", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.verpachtung === "ja" && (
            <div style={{ marginTop: 12 }}>
              <BeteiligtenListe
                listenKey="paechter"
                entries={asBeteiligtenListe(a.paechter)}
                onChange={(next) => onChange("paechter", next)}
                singular="Pächter"
                dialogTitle="Pächter auswählen"
                felder={["einheit", "von", "bis"]}
                labels={{ von: "Pachtbeginn", bis: "Pachtende (leer = unbefristet)" }}
                lookup={contactLookup}
                ansprache={ansprache}
              />
            </div>
          )}
        </div>

        <div>
          <QuestionBlock
            question={{
              question: "Gibt es einen vom Eigentümer abweichenden Betreiber?",
              field: { key: "betreiberAbweichend", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.betreiberAbweichend === "ja" && (
            <div style={cardStyle()}>
              {kontakt("betreiberKontaktRef", "betreiberName", "Betreiber", "Betreiber auswählen")}
              <div style={{ marginTop: 12 }}>
                <QuestionBlock
                  question={{
                    question: "Verantwortungsbereich des Betreibers",
                    field: { key: "betreiberVerantwortungsbereich", type: "text" },
                  }}
                  answers={a}
                  onChange={onChange}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <QuestionBlock
            question={{
              question: "Nutzen weitere Personen oder Unternehmen das Objekt?",
              field: { key: "sonstigeNutzungJa", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.sonstigeNutzungJa === "ja" && (
            <div style={{ marginTop: 12 }}>
              <BeteiligtenListe
                listenKey="sonstigeNutzer"
                entries={asBeteiligtenListe(a.sonstigeNutzer)}
                onChange={(next) => onChange("sonstigeNutzer", next)}
                singular="Nutzer"
                dialogTitle="Nutzer auswählen"
                felder={["art", "einheit"]}
                labels={{ art: "Art der Nutzung" }}
                lookup={contactLookup}
                ansprache={ansprache}
              />
            </div>
          )}
        </div>

        <div>
          <QuestionBlock
            question={{
              question: "Ist ein Facility Management beauftragt?",
              field: { key: "facilityBeauftragt", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.facilityBeauftragt === "ja" && (
            <div style={cardStyle()}>
              {kontakt(
                "facilityKontaktRef",
                "facilityName",
                "Facility Management",
                "Facility Management auswählen",
              )}
              <div style={{ marginTop: 12 }}>
                <QuestionBlock
                  question={{
                    question: "Kontaktdaten des Facility Managements",
                    field: { key: "facilityKontaktdaten", type: "text" },
                  }}
                  answers={a}
                  onChange={onChange}
                />
              </div>
            </div>
          )}
        </div>

        <div>
          <QuestionBlock
            question={{
              question:
                "Gibt es weitere verbundene Personen oder Unternehmen am Objekt?",
              field: { key: "weitereBeteiligteJa", type: "yesno" },
            }}
            answers={a}
            onChange={onChange}
          />
          {a.weitereBeteiligteJa === "ja" && (
            <div style={{ marginTop: 12 }}>
              <BeteiligtenListe
                listenKey="weitereBeteiligte"
                entries={asBeteiligtenListe(a.weitereBeteiligte)}
                onChange={(next) => onChange("weitereBeteiligte", next)}
                singular="Beteiligter"
                dialogTitle="Beteiligten auswählen"
                felder={["art", "einheit", "bemerkung"]}
                labels={{ art: "Art der Verbindung", einheit: "Umfang" }}
                lookup={contactLookup}
                ansprache={ansprache}
              />
            </div>
          )}
        </div>
      </div>
    );
  }

  /**
   * „Unterlagen" — je Katalogeintrag eine Karte. Wie die Karte aussieht, hängt
   * daran, ob an dieser Stelle überhaupt hochgeladen werden kann:
   *
   * - **Upload möglich** (intern, `uploads` gesetzt): direkt der Hochlade-
   *   Bereich, ohne vorgeschaltetes Auswahlfeld — genau wie „Foto / Skizze des
   *   Objekts" im Anschrift-Schritt. Die Bezeichnung trägt das `UploadField`
   *   selbst als Dropzone-Titel.
   * - **Upload abgeschaltet** (beide Kundenwege: `chrome="public"` und die
   *   Token-Strecke `RisikoanalyseDetailFormPublic`, die ohne `uploads`
   *   einbettet): es bleibt beim Auswahlfeld. Dort gibt es keine Dropzone,
   *   sondern nur die Hinweiszeile auf den Upload-Link nach dem Absenden —
   *   ohne das Feld hätte der Schritt auf dem Kundenweg gar KEINE Eingabe mehr
   *   und der Makler erführe nie, welche Unterlagen existieren.
   *
   * Scope (Gebäude-UUID) und Slot-Schlüssel (`unterlagen:<key>`) sind in beiden
   * Fassungen unverändert, sonst verlören bereits hochgeladene Dateien ihre
   * Zuordnung. Auch der Einleitungssatz folgt der Upload-Fähigkeit und nicht
   * der Ansprache — sonst verspräche er einen Bereich, den es darunter nicht
   * gibt (E11).
   *
   * BESTANDSDATEN: Der Status liegt im Antwort-Schlüssel `unterlagen`
   * (`{ [itemKey]: "nicht_vorhanden" | "folgt" | "anbei" }`), Format
   * unverändert. Wo der Hochlade-Bereich steht, wird er nur noch GELESEN und
   * als Zeile „bisher erfasst: …" ausgewiesen — dort nie überschrieben, sonst
   * ginge weg, was jemand erfasst hat.
   */
  function renderUnterlagen(g: Gebaeude): JSX.Element {
    const a = g.answers as Answers;
    const unterlagen = (a.unterlagen as Record<string, string>) ?? {};
    const uploadAktiv = uploadMode.mode !== "disabled";
    const setUnterlagen = (k: string, v: string) => {
      setGebaeudeAnswer(g.id, "unterlagen", { ...unterlagen, [k]: v });
    };
    return (
      <div className={styles.questions}>
        <p style={{ fontFamily: BRAND.fontPrimary, color: BRAND.ink, marginTop: 0, marginBottom: 12 }}>
          {immoText(uploadAktiv ? "unterlagenIntroUpload" : "unterlagenIntro", ansprache)}
        </p>
        {/*
          Karten stehen direkt untereinander — den Abstand macht `cardStyle()`
          (marginBottom), wie in den übrigen Kartenlisten des Formulars. Ein
          zusätzlicher Flex-Gap hat die Liste hier doppelt so weit gesetzt.
        */}
        {UNTERLAGEN_ITEMS.map((item) => {
          const value = unterlagen[item.key] ?? "";
          if (!uploadAktiv) {
            return (
              <div key={item.key} style={cardStyle()}>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                  <span style={{ fontFamily: BRAND.fontPrimary, color: BRAND.ink, flexGrow: 1 }}>
                    {item.label}
                  </span>
                  <select
                    style={inputStyle()}
                    value={value}
                    onChange={(e) => setUnterlagen(item.key, e.target.value)}
                  >
                    <option value="">— wählen —</option>
                    {UNTERLAGEN_STATUS.map((s) => (
                      <option key={s.value} value={s.value}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
                {value === "anbei" && (
                  <div style={{ marginTop: 8 }}>
                    <UploadField
                      scope={g.id}
                      slotKey={`unterlagen:${item.key}`}
                      label={item.label}
                      ansprache={ansprache}
                    />
                  </div>
                )}
              </div>
            );
          }
          const bisher = UNTERLAGEN_STATUS.find((s) => s.value === value);
          return (
            <div key={item.key} style={cardStyle()}>
              <UploadField
                scope={g.id}
                slotKey={`unterlagen:${item.key}`}
                label={item.label}
                ansprache={ansprache}
              />
              {bisher && (
                <span
                  style={{
                    display: "block",
                    marginTop: 8,
                    fontFamily: BRAND.fontPrimary,
                    fontSize: "0.85rem",
                    color: BRAND.muted,
                  }}
                >
                  bisher erfasst: {bisher.label}
                </span>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function renderSummary(): JSX.Element {
    return (
      <div className={styles.summaryGrid}>
        <div className={styles.summaryCard}>
          <div className={styles.summaryHead}>
            <span className={styles.summaryTitle}>Anschrift</span>
            <button
              type="button"
              className={styles.summaryEdit}
              onClick={() => jumpToStepKey("anschrift")}
            >
              Bearbeiten →
            </button>
          </div>
          <div className={styles.rowFact}>
            <span className={styles.rowFactKey}>Adresse</span>
            <span className={styles.rowFactValue}>
              {[
                globalAnswers.street,
                [globalAnswers.postalCode, globalAnswers.city].filter(Boolean).join(" "),
                globalAnswers.country,
              ].filter(Boolean).join(", ") || "—"}
            </span>
          </div>
        </div>
        {gebaeude.map((g) => {
          const a = g.answers;
          const facts: Array<[string, string]> = [
            ["Kategorie / Typ", `${a.kategorie ?? "—"}${a.typ ? ` · ${a.typ}` : ""}`],
            ["Bauartklasse", String(a.bauartklasse ?? "—")],
            ["Baujahr", String(a.baujahr ?? "—")],
            ["Gesamtfläche", a.gesamtflaeche ? `${a.gesamtflaeche} m²` : "—"],
            ...(wohnflaecheGefragt(a)
              ? ([
                  [
                    "Wohnfläche",
                    a.wohnflaeche ? `${a.wohnflaeche} m²` : "fehlt — Pflichtangabe (Schritt „Bauart“)",
                  ],
                ] as Array<[string, string]>)
              : []),
            ["Dach", String(a.dachTyp ?? "—")],
            [
              "Anzahl Einheiten",
              String(
                a.anzahlEinheiten ??
                  (a.einheiten as Einheit[] | undefined)?.length ??
                  "—"
              ),
            ],
            ["Stellplätze", stellplatzZusammenfassung(a)],
            ["Anbauten", anbautenAusAntworten(a).length > 0 ? String(anbautenAusAntworten(a).length) : "—"],
            ["Sanierungen", sanierungenAusAntworten(a).map((e) => e.jahr ?? "o. J.").join(", ") || "—"],
            ["Schäden", schadenZusammenfassung(a)],
            ["Eigentümer", String(a.eigentuemerName ?? "—")],
          ];
          return (
            <div key={g.id} className={styles.summaryCard}>
              <div className={styles.summaryHead}>
                <span className={styles.summaryTitle}>{g.label}</span>
                <button
                  type="button"
                  className={styles.summaryEdit}
                  onClick={() => jumpToStepKey(`${g.id}-gebaeudeart`)}
                >
                  Bearbeiten →
                </button>
              </div>
              {facts.map(([k, v]) => (
                <div key={k} className={styles.rowFact}>
                  <span className={styles.rowFactKey}>{k}</span>
                  <span className={styles.rowFactValue}>{v}</span>
                </div>
              ))}
            </div>
          );
        })}
        {renderDateienKarte()}
      </div>
    );
  }

  /**
   * „Dateien" in der Zusammenfassung. Ohne sie wäre vor dem Speichern nirgends
   * sichtbar, was gleich mit hochgeht — und im Modus `buffer` ist genau das
   * die letzte Gelegenheit, es zu bemerken.
   */
  function renderDateienKarte(): JSX.Element | null {
    if (uploadMode.mode === "disabled") return null;
    const vorgemerkt = uploadBuffer.pending;
    const labelFuerScope = (scope: string): string =>
      scope === GLOBAL_SCOPE
        ? "Objekt"
        : (gebaeude.find((g) => g.id === scope)?.label ?? "Gebäude");
    return (
      <div className={styles.summaryCard}>
        <div className={styles.summaryHead}>
          <span className={styles.summaryTitle}>Dateien</span>
        </div>
        {vorgemerkt.length === 0 ? (
          <div className={styles.rowFact}>
            <span className={styles.rowFactKey}>Vorgemerkt</span>
            <span className={styles.rowFactValue}>keine</span>
          </div>
        ) : (
          vorgemerkt.map((p) => (
            <div key={p.id} className={styles.rowFact}>
              <span className={styles.rowFactKey}>
                {labelFuerScope(p.scope)} · {p.label}
              </span>
              <span className={styles.rowFactValue}>{p.file.name}</span>
            </div>
          ))
        )}
        {uploadMode.mode === "buffer" && vorgemerkt.length > 0 && (
          <div className={styles.rowFact}>
            <span className={styles.rowFactKey}>Hinweis</span>
            <span className={styles.rowFactValue}>
              Die Dateien werden direkt nach dem Speichern hochgeladen.
            </span>
          </div>
        )}
      </div>
    );
  }

  // ── render shell ───────────────────────────────
  const embeddedSubmitLabel =
    isEmbedded && (props as Extract<RisikoImmobilieStepFormProps, { chrome: "embedded" }>).embeddedSubmitLabel
      ? (props as Extract<RisikoImmobilieStepFormProps, { chrome: "embedded" }>).embeddedSubmitLabel!
      : "Immobilie speichern";

  /**
   * Kontextzeile: Fenster-ID, Kundenname und Schließen-Weg gibt es nur auf dem
   * eingebetteten Backoffice-Weg. Der Text selbst entsteht auch öffentlich —
   * dort ohne Kundennamen (den zeigt die TopBar).
   */
  const kontextProps = props.chrome === "embedded" ? props : null;
  const kontextText = kontextZeileText({
    street: globalAnswers.street,
    postalCode: globalAnswers.postalCode,
    city: globalAnswers.city,
    kundenName: kontextProps?.kundenName ?? null,
  });

  // Ein gemeinsamer Satz Verzeichnis-Props für beide Darstellungen — die
  // Sidebar links und das Panel hinter dem Knopf zeigen dasselbe.
  const tocProps = {
    sections,
    currentSectionIdx: sectionIdx,
    currentSubStepIdx: subStepIdx,
    onSelect: handleSelect,
    onSectionSelect: handleSectionSelect,
    // Eine ruhige Auswahl-Optik statt vier rotierender Abschnittsfarben
    // (Auftraggeber, 18.08.2026). Gilt NUR hier — Risikoanalyse-Kurzform, KFZ
    // und Kennzahlen nutzen dieselbe `Sidebar` und bleiben unverändert.
    farbschema: "neutral" as const,
    progressFraction: overallFraction,
    progressDetail: `${progress.answered}/${progress.total} Felder`,
    eyebrow: "Immobilie",
    heading: "Inhaltsverzeichnis",
    ariaLabel: "Inhaltsverzeichnis der Immobilien-Erfassung",
    scopeSwitcher: {
      label: "Gebäude",
      items: gebaeude.map((g, idx) => ({ id: g.id, label: g.label || `Gebäude ${idx + 1}` })),
      activeId: tocGebaeudeId,
      onChange: handleGebaeudeWechsel,
    },
  };

  return (
    <div
      ref={shellRef}
      className={mergeClasses(
        styles.shell,
        isEmbedded && styles.shellEmbedded,
        inhaltshoehe && styles.shellInhalt,
      )}
      /*
        Linker Rand der Schritt-Leiste: sie beginnt in der Vollseite am rechten
        Rand des Inhaltsverzeichnisses, damit das Verzeichnis bis zum unteren
        Rand durchläuft. Ohne Verzeichnis (Hamburger-Modus) bleibt die Variable
        ungesetzt und die Leiste nimmt die volle Breite.

        Der Wert ist GEMESSEN, nicht `SIDEBAR_WIDTH`: Bezugsrahmen der absolut
        positionierten Leiste ist die Fenster-Schale, und das Verzeichnis
        beginnt nicht an deren linkem Rand, sondern eingerückt — der Fehlbetrag
        wanderte zusätzlich mit der Breite der App-Navigation. Bis die erste
        Messung steht, bleibt es beim bisherigen Wert.
      */
      style={
        tocLayout === "sidebar"
          ? ({
              "--toc-leiste-breite": `${tocLeistenOffset ?? SIDEBAR_WIDTH}px`,
            } as React.CSSProperties)
          : undefined
      }
    >
      {!isEmbedded && (
        // Genau ein Auslöser je Kontext: das Verzeichnis hängt jetzt am
        // `TocMenu` im Formular, nicht mehr am Hamburger der TopBar.
        <TopBar customerName={companyName} menuMode="never" />
      )}
      <div className={mergeClasses(styles.body, inhaltshoehe && styles.bodyInhalt)}>
        {tocLayout === "sidebar" && (
          <aside
            /*
              Weder gemessene Höhe noch `position: sticky`: die Schale hat eine
              definite Höhe, also streckt der Flex-Container das `<aside>` in
              jedem Kontext bis zum unteren Rand. Der gemessene Wert war
              eingebettet nötig (dort scrollte der Fenster-Body) und in der
              Vollseite zu groß — er kannte die Kopfzeile nicht.
            */
            ref={verzeichnisRef}
            className={styles.sidebarDesktop}
          >
            <Sidebar
              {...tocProps}
              /*
                Nur noch Sicherung: die Höhe kommt aus dem Flex-Layout. In einem
                Rahmen OHNE definite Höhe (öffentliche Detail-Strecke) fiele die
                Schale auf Inhaltshöhe zurück — dann deckelt der gemessene Wert
                des äußeren Scrollports das Verzeichnis wie bisher.
              */
              maxHeight={isEmbedded ? availableHeight : undefined}
              /*
                Vollseite: die Schritt-Leiste läuft dort über die volle Breite
                und liegt über dem Fuss des Verzeichnisses (#1307) — so viel
                Freiraum unter dem letzten Eintrag hält ihn erreichbar.
              */
              bottomInset={isEmbedded && istVollseite ? BOTTOM_BAR_RESERVE : 0}
            />
          </aside>
        )}
        <main
          className={mergeClasses(styles.main, inhaltshoehe && styles.mainInhalt)}
          ref={mainRef}
        >
          <div className={mergeClasses(styles.inner, isEmbedded && styles.innerEmbedded)}>
            {tocLayout === "menu" && (
              <TocMenu
                {...tocProps}
                maxHeight={availableHeight}
                kontextText={`Schritt ${stepIdx + 1} von ${totalSteps}${
                  currentG ? ` · ${currentG.label}` : ""
                }`}
              />
            )}
            {!isEmbedded && (
              <div className={styles.sectionPill}>
                {section.title}
                {step.kind !== "anschrift" && step.kind !== "summary" && currentG && ` · ${currentG.label}`}
              </div>
            )}
            {/*
              Kontextzeile statt Kopfzeile: links, worauf sich der Schritt
              bezieht, rechts die Bedienung der Vollseite. `PageModeControls`
              liest den WindowManager-Context — deshalb NUR mit `windowId`
              rendern: die öffentlichen Wege laufen ohne Provider und der Hook
              würde dort werfen. Ohne Vollseite gibt die Komponente selbst
              `null` zurück (im schwebenden Fenster bedient die Titelleiste).
            */}
            <div
              className={mergeClasses(
                styles.kontextZeile,
                isEmbedded && istVollseite && styles.kontextZeileSticky,
              )}
            >
              <span className={styles.kontextText} title={kontextText}>
                {kontextText}
              </span>
              {kontextProps?.windowId && (
                <PageModeControls
                  variant="icons"
                  windowId={kontextProps.windowId}
                  onClose={kontextProps.onCloseRequest}
                />
              )}
            </div>
            <div className={styles.titleRow}>
              <h2 className={styles.title}>{subStepTitle}</h2>
              <StepHero kind={heroKind} />
            </div>
            {stepDescription && <p className={styles.description}>{stepDescription}</p>}
            {/*
              Der Provider umschließt nur den Schritt-Inhalt: alle
              `UploadField`-Instanzen sitzen darin. Die Zusammenfassung liest
              den Puffer direkt aus dem State (`uploadBuffer`), nicht über den
              Context — sie braucht ihn schon beim Rendern der Karte „Dateien".
            */}
            <UploadBufferProvider value={uploadBuffer}>
              {/*
                `data-testid` ist hier kein Beiwerk:
                `pages/risikoImmobilieZusammengelegteSchritte.test.tsx` läuft
                über `SCHRITT_INHALT_TESTID` die ganze Kette ab und prüft je
                Schritt, dass dieser Behälter nicht leer ist. Ohne einen
                benannten Anker ließe sich „leere Seite" nicht von
                „Seitenrahmen ohne Inhalt" trennen.
              */}
              <div data-testid={SCHRITT_INHALT_TESTID}>{renderBody()}</div>
            </UploadBufferProvider>
            {isEmbedded && (
              <BottomBar
                sticky
                onPrev={goPrev}
                onNext={goNext}
                onSubmit={handleSubmit}
                onSaveDraft={() => {}}
                canPrev={stepIdx > 0}
                isLastStep={stepIdx === totalSteps - 1}
                submitting={submitting}
                submitDisabled={submitLocked}
                savingDraft={false}
                draftSavedAt={null}
                progressLabel={`Schritt ${stepIdx + 1} von ${totalSteps}`}
                progressFraction={stepFraction}
                submitLabel={embeddedSubmitLabel}
                submittingLabel="Speichere…"
                showSaveDraft={false}
              />
            )}
          </div>
        </main>
      </div>
      {!isEmbedded && (
        <div
          className={mergeClasses(
            styles.bottomBarFixed,
            tocLayout === "sidebar" && styles.bottomBarFixedNebenSidebar,
          )}
        >
          {serverFehler && (
            <div
              role="alert"
              style={{
                fontFamily: BRAND.fontPrimary,
                fontSize: "0.85rem",
                color: "#a4262c",
                background: "#fdf3f4",
                padding: "8px 16px",
                borderTop: "1px solid #f1c8cc",
              }}
            >
              {serverFehler}
            </div>
          )}
          <BottomBar
            onPrev={goPrev}
            onNext={goNext}
            onSubmit={handleSubmit}
            onSaveDraft={() => {}}
            canPrev={stepIdx > 0}
            isLastStep={stepIdx === totalSteps - 1}
            submitting={submitting}
            draftSavedAt={draftSavedAt}
            progressLabel={`Schritt ${stepIdx + 1} von ${totalSteps} · ${section.title}`}
            progressFraction={stepFraction}
            submitLabel="Absenden"
            showSaveDraft={false}
          />
        </div>
      )}
    </div>
  );
};

/**
 * Beschreibungstext eines Schritts. Die Schritte, die dieser PR anfasst,
 * kennen zwei Fassungen (Kundenformular vs. interner Beratermodus, E11); die
 * übrigen sind identisch und ziehen in ihren eigenen PRs nach.
 *
 * Der Rückgabetyp ist `string`, nicht `string | undefined` — und das ist der
 * ganze Punkt: nur so meldet der Compiler eine vergessene Art (TS2366,
 * „lacks ending return statement"), genau wie bei `titleForImmoStepKind`.
 * `noImplicitReturns` ist in `apps/web/tsconfig.json` NICHT gesetzt; mit
 * `| undefined` fiele ein fehlender `case` still durch, der neue Schritt ginge
 * wegen `{stepDescription && …}` ohne Erklärtext live, und niemand merkte es.
 */
const descriptionForKind = (
  k: ImmoStepKind,
  ansprache: Ansprache,
): string => {
  switch (k) {
    case "anschrift":
      return immoText("beschreibungAnschrift", ansprache);
    case "gebaeudeart":
      return "Bitte wählen Sie die Kategorie und den Typ des Gebäudes.";
    case "spezifizierung":
      return "Geschosse, Bauartklasse, Baujahr, Sanierung, Nutzung und Leerstand.";
    case "dach":
      return "Welche Dachform hat das Gebäude?";
    case "einheiten_anzahl":
      return "Wie viele Einheiten enthält das Gebäude, welche Eckdaten haben sie — und gibt es Kunst am Bau?";
    case "branchen":
      return "Sind im Gebäude besonders risikoreiche Branchen tätig?";
    case "aussenanlagen":
      return "Wertrelevante Außenanlagen oder Grundstücksbestandteile?";
    case "bauausfuehrung":
      return "Bauausführung, Ausstattung und Sonderbestandteile.";
    case "sicherheit":
      return "Einrichtungen zur Sicherheit, Schadenverhütung oder Gefahrenprävention.";
    case "praevention":
      return "Systeme, die einen Schaden früh erkennen oder ihm vorbeugen.";
    case "energie":
      return "Heizungsarten und wärmetechnische Anlagen — Mehrfachauswahl möglich.";
    case "umwelt":
      return "Umweltrelevante Anlagen oder Einrichtungen — Mehrfachauswahl möglich.";
    case "anbauten":
      return "Anbauten und weitere Gebäude auf dem Grundstück — je mit Bauweise, Dach und Baujahr.";
    case "eigentum":
      return immoText("beschreibungEigentum", ansprache);
    case "beteiligte":
      return immoText("beschreibungBeteiligte", ansprache);
    case "gutachten":
      return immoText("beschreibungGutachten", ansprache);
    case "finanzierung":
      return immoText("beschreibungFinanzierung", ansprache);
    case "versicherungsbedarf":
      return immoText("beschreibungVersicherungsbedarf", ansprache);
    case "unterlagen":
      return immoText("beschreibungUnterlagen", ansprache);
    case "summary":
      return immoText("beschreibungSummary", ansprache);
  }
};

// inline style helpers used inside Bauausführung / Unterlagen
/**
 * „Garage (3 Stellplätze, außerhalb) · Carport (1 Stellplätze)" für die
 * Zusammenfassung. Ein bewusstes „nein" am Gate wird als „keine" ausgewiesen —
 * sonst wäre es von „nie ausgefüllt" nicht zu unterscheiden.
 *
 * Das Gate wird **zuerst** ausgewertet: sagt der Bearbeiter bei einem
 * Altdatensatz „nein", blendet das Formular die Liste aus — dann darf die
 * Zusammenfassung nicht weiterhin Stellplätze zeigen, die die Maske gerade
 * verneint. Dass die Altwerte gespeichert bleiben, steht dabei ausdrücklich da.
 */
const stellplatzZusammenfassung = (a: Record<string, unknown>): string => {
  const arten = asStellplatzArten(a.stellplatzArten);
  const details = asStellplatzDetails(a.stellplatzDetails);
  if (a.stellplatzVorhanden === "nein")
    return arten.length > 0
      ? "keine (frühere Angaben bleiben gespeichert)"
      : "keine";
  return stellplatzArtenText(arten, details) || "—";
};

/** „2 Schäden erfasst" bzw. „keine" — die Zeilen selbst stehen im Schritt „Bauart". */
const schadenZusammenfassung = (a: Record<string, unknown>): string => {
  const anzahl = asSchadenListe(a.schaeden).length;
  if (anzahl > 0) return `${anzahl} erfasst`;
  if (a.schaedenJa === "nein") return "keine";
  if (typeof a.schaedenDetails === "string" && a.schaedenDetails.trim() !== "")
    return "als Freitext erfasst";
  return "—";
};

const headingStyle = (): React.CSSProperties => ({
  fontFamily: BRAND.fontAccent,
  fontSize: "1.1rem",
  fontWeight: 500,
  color: BRAND.ink,
  marginTop: 0,
  marginBottom: 10,
});
const cardStyle = (): React.CSSProperties => ({
  backgroundColor: BRAND.white,
  border: `1px solid ${BRAND.borderDashed}`,
  borderRadius: 8,
  padding: "12px 14px",
  marginBottom: 8,
});
const revealStyle = (): React.CSSProperties => ({
  marginTop: 10,
  paddingTop: 10,
  paddingLeft: 12,
  paddingRight: 12,
  paddingBottom: 10,
  backgroundColor: "#FFF8E8",
  borderLeft: `3px solid ${BRAND.gold}`,
  borderRadius: 4,
  display: "grid",
  gap: 10,
  gridTemplateColumns: "1fr 1fr",
});
const smallLabelStyle = (): React.CSSProperties => ({
  fontFamily: BRAND.fontPrimary,
  fontSize: "0.72rem",
  fontWeight: 600,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: "#5d5664",
});
const inputStyle = (): React.CSSProperties => ({
  width: "100%",
  boxSizing: "border-box",
  fontFamily: BRAND.fontPrimary,
  fontSize: "0.92rem",
  color: BRAND.ink,
  backgroundColor: BRAND.white,
  padding: "8px 10px",
  borderRadius: 4,
  border: "1px solid #d4cfd9",
  outline: "none",
});

const RisikoImmobilieStepFormPublic = () => <RisikoImmobilieStepForm chrome="public" />;

export { RisikoImmobilieStepForm };
export default RisikoImmobilieStepFormPublic;
